import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from "react";
import apiClient from "../../api/apiClient";
import { getDashboardSummary, getMonthlyLeaves, getCompanyPolicies, uploadCompanyPolicy, updateCompanyPolicy, deleteCompanyPolicy, getAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, getLocations } from "../../api/accountsService";
import { getEditTickets, resolveTicket, editAttendance, editLeave, getRemoteAttendance, resolveRemoteAttendance, getWfhPeriods, resolveWfhPeriod, raiseEditTicket } from "../../api/attendanceService";
import MarkAttendance from "../shared/MarkAttendance";
import { getSupportStats } from "../../api/chatService";
import useAuthStore from "../../store/authStore";

const RequestLeave = React.lazy(() => import("../shared/RequestLeave"));
const AttendanceReport = React.lazy(() => import("./AttendanceReport"));
const LeaveReport = React.lazy(() => import("./LeaveReport"));
const LiveSupport = React.lazy(() => import("./LiveSupport"));


function isTodayBirthday(dobIso) {
  if (!dobIso) return false;
  const [, mm, dd] = dobIso.split("-");
  const today = new Date();
  return (
    parseInt(mm, 10) === today.getMonth() + 1 &&
    parseInt(dd, 10) === today.getDate()
  );
}

function sanitizeText(str) {
  if (!str) return "";
  return str
    .replace(/<[^>]*>/g, "") // strip HTML tags
    .replace(/[;'"\\]/g, "") // strip SQL character sets
    .trim();
}

// ─── Framer variants ──────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 6 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.03, duration: 0.15, ease: "easeOut" },
  }),
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
};

// ─── Role metadata ────────────────────────────────────────────────────────────
const ROLE_META = {
  ADMIN: { label: "Admin", color: "bg-violet-500/20 text-violet-300 border-violet-500/30", dot: "bg-violet-400" },
  MANAGER: { label: "Manager", color: "bg-blue-500/20 text-blue-300 border-blue-500/30", dot: "bg-blue-400" },
  EMPLOYEE: { label: "Employee", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400" },
};

const AVG = [
  "from-violet-500 to-purple-600", "from-sky-500 to-blue-600",
  "from-teal-500 to-emerald-600", "from-rose-500 to-pink-600",
  "from-amber-500 to-orange-600", "from-indigo-500 to-violet-600",
];
const avGrad = (n = "") => AVG[(n.charCodeAt(0) || 0) % AVG.length];

const LEAVE_STYLES = {
  "Casual Leave": { pill: "bg-sky-500/15 text-sky-300 border-sky-500/25", cal: "bg-amber-400", label: "bg-amber-400/90" },
  "Half Day Casual": { pill: "bg-cyan-500/15 text-cyan-300 border-cyan-500/25", cal: "bg-teal-400", label: "bg-teal-400/90" },
  "Sick Leave": { pill: "bg-rose-500/15 text-rose-300 border-rose-500/25", cal: "bg-violet-500", label: "bg-violet-500/90" },
  "Half day": { pill: "bg-violet-500/15 text-violet-300 border-violet-500/25", cal: "bg-teal-400", label: "bg-teal-400/90" },
  "Other": { pill: "bg-slate-500/15 text-slate-300 border-slate-500/25", cal: "bg-slate-400", label: "bg-slate-400/90" },
  "Comp Off": { pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25", cal: "bg-emerald-400", label: "bg-emerald-400/90" },
};
const leaveStyle = (t) => {
  if (!t) return LEAVE_STYLES["Other"];
  if (LEAVE_STYLES[t]) return LEAVE_STYLES[t];
  const lower = t.toLowerCase();
  if (lower.includes("casual") && lower.includes("half")) return LEAVE_STYLES["Half Day Casual"];
  if (lower.includes("casual")) return LEAVE_STYLES["Casual Leave"];
  if (lower.includes("sick")) return LEAVE_STYLES["Sick Leave"];
  if (lower.includes("half")) return LEAVE_STYLES["Half day"];
  if (lower.includes("comp")) return LEAVE_STYLES["Comp Off"];
  return LEAVE_STYLES["Other"];
};

function normalizePhoto(photo) {
  if (!photo) return null;
  const base = photo.startsWith("http") ? photo : photo.startsWith("/") ? photo : `/media/${photo}`;
  return `${base}?t=${Date.now()}`;
}

const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];



const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

// ─── Glass card ───────────────────────────────────────────────────────────────
function GlassCard({ children, className = "" }) {
  return (
    <div className={`bg-white/[0.05] backdrop-blur-2xl border border-white/[0.08]
      rounded-2xl shadow-[0_4px_32px_rgba(0,0,0,0.3)] ${className}`}>
      {children}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
function Avatar({ photo, name, size = 36 }) {
  const [imgErr, setImgErr] = useState(false);
  const ini = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  if (photo && !imgErr) return (
    <img
      src={photo} alt={name}
      className="rounded-full object-cover flex-shrink-0 ring-1 ring-white/15"
      style={{ width: size, height: size }}
      onError={() => setImgErr(true)}
    />
  );
  return (
    <div
      className={`rounded-full bg-gradient-to-br ${avGrad(name)} flex items-center
        justify-center text-white font-bold flex-shrink-0 shadow-md`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >{ini}</div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center items-center py-10">
      <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" />
    </div>
  );
}



// ════════════════════════════════════════════════════════════════════════════
// PARTY BACKGROUND (unchanged)
// ════════════════════════════════════════════════════════════════════════════
const PARTY_COLORS = [
  "#f59e0b", "#10b981", "#6366f1", "#ec4899", "#3b82f6",
  "#f97316", "#a855f7", "#14b8a6", "#ef4444", "#eab308",
  "#06b6d4", "#84cc16", "#f43f5e", "#8b5cf6", "#0ea5e9",
];

const PartyBackground = memo(function PartyBackground({ active }) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const N = 80;
    particlesRef.current = Array.from({ length: N }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight - window.innerHeight,
      w: 6 + Math.random() * 10,
      h: 8 + Math.random() * 14,
      color: PARTY_COLORS[Math.floor(Math.random() * PARTY_COLORS.length)],
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.08,
      vx: (Math.random() - 0.5) * 1.2,
      vy: 0.8 + Math.random() * 1.4,
      alpha: 0.6 + Math.random() * 0.4,
      isCircle: Math.random() > 0.6,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach(p => {
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.isCircle) {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.rotV;
        p.vx += (Math.random() - 0.5) * 0.05;
        if (p.y > window.innerHeight + 20) {
          p.y = -20;
          p.x = Math.random() * window.innerWidth;
        }
      });
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.35 }}
    />
  );
});

const PartyDecorations = memo(function PartyDecorations({ active }) {
  const decorations = useMemo(() => {
    if (!active) return [];
    return Array.from({ length: 18 }, (_, i) => ({
      id: i,
      emoji: ["🎈", "🎊", "🎉", "🎁", "⭐", "🌟", "✨", "🎆", "🎇", "🎀"][i % 10],
      x: `${5 + (i * 5.5) % 90}%`,
      y: `${10 + (i * 17 + 30) % 75}%`,
      size: 20 + (i % 4) * 8,
      delay: i * 0.3,
      duration: 3 + (i % 3) * 1.5,
    }));
  }, [active]);

  if (!active) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {decorations.map(d => (
        <motion.div
          key={d.id}
          className="absolute select-none"
          style={{ left: d.x, top: d.y, fontSize: d.size, opacity: 0.25 }}
          animate={{ y: [0, -18, 0, 12, 0], rotate: [-8, 8, -5, 5, -8], scale: [1, 1.15, 1, 0.9, 1] }}
          transition={{ repeat: Infinity, duration: d.duration, delay: d.delay, ease: "easeInOut" }}
        >{d.emoji}</motion.div>
      ))}
      <div className="absolute top-0 left-0 w-72 h-72 pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(251,191,36,0.06) 0%, transparent 70%)" }} />
      <div className="absolute top-0 right-0 w-72 h-72 pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(139,92,246,0.06) 0%, transparent 70%)" }} />
      <div className="absolute bottom-0 right-0 w-72 h-72 pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(16,185,129,0.06) 0%, transparent 70%)" }} />
    </div>
  );
});

// ════════════════════════════════════════════════════════════════════════════
// BIRTHDAY POPUP
// ════════════════════════════════════════════════════════════════════════════
function ConfettiParticle({ left, top, size, color, isCircle, yEnd, xDrift, rotateDeg, duration, delay }) {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{ left, top, width: size, height: size, backgroundColor: color, borderRadius: isCircle ? "50%" : "2px" }}
      initial={{ y: 0, opacity: 1, rotate: 0, x: 0 }}
      animate={{ y: yEnd, opacity: [1, 1, 0], rotate: rotateDeg, x: xDrift }}
      transition={{ duration, ease: "easeIn", delay }}
    />
  );
}

function BirthdayPopup({ name, gender = "MALE", onClose, onClosed }) {
  const isFemale = gender === "FEMALE";

  // Use a softer, custom palette for watercolor vs bright neon colors for male
  const CONFETTI_COLORS = isFemale
    ? ["#fda4af", "#fecdd3", "#ffe4e6", "#f472b6", "#c084fc", "#e879f9", "#eab308", "#d97706"]
    : ["#3b82f6", "#a855f7", "#ec4899", "#06b6d4", "#10b981", "#6366f1", "#f43f5e", "#f59e0b"];

  const confetti = useRef(
    Array.from({ length: 90 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${-5 - Math.random() * 15}%`,
      size: `${5 + Math.random() * 9}px`,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      isCircle: Math.random() > 0.4,
      yEnd: `${80 + Math.random() * 40}vh`,
      xDrift: `${(Math.random() - 0.5) * 120}px`,
      rotateDeg: Math.random() > 0.5 ? 720 : -720,
      duration: 2.8 + Math.random() * 2.2,
      delay: Math.random() * 1.8,
    }))
  ).current;

  useEffect(() => {
    const t = setTimeout(() => { onClose(); onClosed(); }, 20000);
    return () => clearTimeout(t);
  }, [onClose, onClosed]);

  const handleClose = () => { onClose(); onClosed(); };

  if (isFemale) {
    // ════════════════════════════════════════════════════════════════════════
    // FEMALE THEME (WATERCOLOR FLORAL / ROSE GOLD)
    // ════════════════════════════════════════════════════════════════════════
    return (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[999] flex items-center justify-center p-4 select-none"
        style={{ background: "rgba(10, 5, 12, 0.45)", backdropFilter: "blur(12px)" }}
        onClick={handleClose}
      >
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {confetti.map(p => <ConfettiParticle key={p.id} {...p} />)}
        </div>

        <motion.div
          initial={{ scale: 0.72, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.85, opacity: 0, y: 15 }}
          transition={{ type: "spring", bounce: 0.32, duration: 0.9 }}
          onClick={e => e.stopPropagation()}
          className="relative w-full overflow-y-auto no-scrollbar"
          style={{
            maxWidth: "min(480px, calc(100vw - 2rem))",
            maxHeight: "92vh",
            background: "linear-gradient(165deg, #FFF6F7 0%, #FFFDFB 60%, #FAF0F3 100%)",
            border: "1.5px solid #FDF2F4",
            borderRadius: "36px",
            boxShadow: "0 30px 90px rgba(157,23,77,0.18), 0 0 0 1px rgba(255,255,255,0.7), inset 0 2px 4px rgba(255,255,255,0.8)",
          }}
        >
          {/* Inner Golden border lines */}
          <div className="absolute inset-2 border border-amber-600/15 rounded-[28px] pointer-events-none z-10" />
          <div className="absolute inset-2.5 border border-amber-600/5 rounded-[26px] pointer-events-none z-10" />

          {/* SVG Watercolor Flower Corner Left-Top */}
          <svg className="absolute -top-12 -left-12 w-48 h-48 opacity-75 pointer-events-none z-0" viewBox="0 0 200 200">
            <defs>
              <filter id="wc-blur-top" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="11" result="blur" />
                <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="3" result="noise" />
                <feDisplacementMap in="blur" in2="noise" scale="14" xChannelSelector="R" yChannelSelector="G" />
              </filter>
            </defs>
            <circle cx="90" cy="90" r="46" fill="#fecdd3" filter="url(#wc-blur-top)" opacity="0.85" />
            <circle cx="110" cy="80" r="32" fill="#fda4af" filter="url(#wc-blur-top)" opacity="0.65" />
            <circle cx="75" cy="100" r="34" fill="#ffe4e6" filter="url(#wc-blur-top)" opacity="0.9" />
            <circle cx="60" cy="70" r="28" fill="#fbcfe8" filter="url(#wc-blur-top)" opacity="0.7" />
            <path d="M90 90 Q125 55 165 65 Q135 105 90 90" fill="#d97706" opacity="0.32" />
            <path d="M90 90 Q50 125 60 165 Q105 135 90 90" fill="#d97706" opacity="0.32" />
          </svg>

          {/* SVG Watercolor Flower Corner Right-Bottom */}
          <svg className="absolute -bottom-16 -right-16 w-52 h-52 opacity-75 pointer-events-none z-0" viewBox="0 0 200 200">
            <defs>
              <filter id="wc-blur-bot" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="12" result="blur" />
                <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="3" result="noise" />
                <feDisplacementMap in="blur" in2="noise" scale="15" xChannelSelector="R" yChannelSelector="G" />
              </filter>
            </defs>
            <circle cx="110" cy="110" r="48" fill="#fecdd3" filter="url(#wc-blur-bot)" opacity="0.8" />
            <circle cx="90" cy="120" r="35" fill="#fda4af" filter="url(#wc-blur-bot)" opacity="0.75" />
            <circle cx="125" cy="90" r="30" fill="#fbcfe8" filter="url(#wc-blur-bot)" opacity="0.7" />
            <path d="M110 110 Q145 75 155 35 Q115 75 110 110" fill="#d97706" opacity="0.32" />
            <path d="M110 110 Q75 145 35 155 Q75 115 110 110" fill="#d97706" opacity="0.32" />
          </svg>

          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full flex items-center justify-center bg-rose-50 hover:bg-rose-100 text-rose-800/60 hover:text-rose-900 border border-rose-100 transition-all text-sm font-semibold"
          >✕</button>

          <div className="relative px-6 sm:px-9 pt-7 pb-6 text-center z-10">
            {/* Elegant Cake Vector Graphic */}
            <div className="mb-3 relative">
              <svg className="mx-auto w-24 h-24 filter drop-shadow-[0_4px_10px_rgba(251,113,133,0.15)]" viewBox="0 0 100 100">
                {/* Cake Pedestal Stand */}
                <ellipse cx="50" cy="85" rx="26" ry="6" fill="#F4E8E9" stroke="#c29b47" strokeWidth="1.5" />
                <path d="M44 85 L47 75 L53 75 L56 85 Z" fill="#EAD0D3" stroke="#c29b47" strokeWidth="1" />
                <ellipse cx="50" cy="74" rx="22" ry="5" fill="#FAF1F2" stroke="#c29b47" strokeWidth="1.5" />

                {/* Cake Body */}
                <rect x="31" y="44" width="38" height="26" rx="3" fill="#FFF" stroke="#c29b47" strokeWidth="1.5" />
                {/* Glaze Drops */}
                <path d="M31 44 Q36 49 40 44 Q44 49 48 44 Q53 49 57 44 Q62 49 69 44 L69 50 Q61 54 58 50 Q52 54 48 50 Q43 54 39 50 Q34 54 31 50 Z" fill="#FBCFE8" />
                <circle cx="38" cy="56" r="2" fill="#FB7185" />
                <circle cx="50" cy="59" r="2.2" fill="#F59E0B" />
                <circle cx="62" cy="55" r="2" fill="#C084FC" />

                {/* Cake Top frosting */}
                <ellipse cx="50" cy="44" rx="19" ry="4.5" fill="#FFF" stroke="#c29b47" strokeWidth="1.5" />
                <ellipse cx="50" cy="44" rx="16" ry="3.5" fill="#FFF2F4" />

                {/* Three Candles */}
                <rect x="42" y="24" width="2" height="18" fill="#FBCFE8" stroke="#c29b47" strokeWidth="0.75" />
                <rect x="49" y="21" width="2" height="21" fill="#FEF08A" stroke="#c29b47" strokeWidth="0.75" />
                <rect x="56" y="24" width="2" height="18" fill="#C084FC" stroke="#c29b47" strokeWidth="0.75" />

                {/* Candle Flames */}
                <path d="M43 18 C43 18 41 21 43 23 C45 23 45 18 43 18 Z" fill="#F59E0B" />
                <path d="M50 14 C50 14 48 18 50 20 C52 20 52 14 50 14 Z" fill="#F59E0B" />
                <path d="M57 18 C57 18 55 21 57 23 C59 23 59 18 57 18 Z" fill="#F59E0B" />
              </svg>

              {/* Delicate Gold Sparkles */}
              <div className="absolute top-6 left-12 text-sm text-yellow-600/60 animate-pulse">✦</div>
              <div className="absolute top-10 right-14 text-base text-yellow-600/50 animate-pulse delay-700">✦</div>
              <div className="absolute bottom-6 right-10 text-xs text-yellow-600/60 animate-pulse delay-300">✦</div>
            </div>

            <motion.h4
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="text-stone-500 font-serif italic text-base leading-none mb-1.5"
            >Dear</motion.h4>

            <motion.h2
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="font-serif font-black leading-tight text-2xl sm:text-[1.65rem] tracking-tight mb-1.5"
              style={{ color: "#9D174D" }}
            >{name},</motion.h2>

            <motion.h2
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className="font-serif italic font-normal text-3xl sm:text-[2.35rem] leading-none mb-4"
              style={{
                background: "linear-gradient(90deg, #D97706 0%, #C29B47 50%, #B45309 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0px 0.5px 0.5px rgba(0,0,0,0.03))"
              }}
            >
              Happy Birthday! <span className="inline-block text-rose-400 text-lg align-middle translate-y-[-2px]">❤️</span>
            </motion.h2>

            {/* Banner Strip */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.45 }}
              className="mb-4 p-2 rounded-2xl flex items-center justify-center gap-1.5 border border-rose-200/50 bg-rose-50/50"
            >
              <span className="w-6 h-6 rounded-full bg-rose-500/10 flex items-center justify-center text-xs">🌸</span>
              <p className="text-stone-600 text-[11px] font-semibold tracking-wide">
                Warm birthday wishes from all of us at <strong className="text-rose-700 font-bold">Sahvia!</strong> 🌸✨
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
              className="text-stone-600 text-[12px] sm:text-[12.8px] leading-relaxed mb-4 font-medium text-center max-w-[350px] mx-auto space-y-2"
            >
              <p>
                Today, we celebrate not just your birthday, but also the positivity, dedication, and kindness you bring every day.
              </p>
              <p>
                Your efforts and spirit truly make a difference, and we’re grateful to have you as part of the <strong className="text-rose-700">Sahvia</strong> family.
              </p>
            </motion.div>

            {/* Premium Floral Quote Frame */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.65 }}
              className="relative my-3.5 p-4 rounded-2xl border border-amber-600/15"
              style={{ background: "rgba(255, 255, 255, 0.4)", backdropFilter: "blur(4px)" }}
            >
              {/* Filigree Corner flourishes */}
              <div className="absolute top-1.5 left-2 text-[9px] text-amber-600/40">✿</div>
              <div className="absolute top-1.5 right-2 text-[9px] text-amber-600/40">✿</div>
              <div className="absolute bottom-1.5 left-2 text-[9px] text-amber-600/40">✿</div>
              <div className="absolute bottom-1.5 right-2 text-[9px] text-amber-600/40">✿</div>

              <p className="text-amber-800/80 text-[16px] mb-1">❝</p>
              <p className="text-stone-600/90 text-[12px] font-serif italic leading-relaxed px-2">
                “May this special year bring you happiness, success, good health, and countless beautiful moments to cherish.”
              </p>
              <p className="text-amber-800/80 text-[16px] mt-1 select-none">❞</p>
            </motion.div>

            {/* Stars indicator */}
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <motion.span key={i} className="text-xs"
                  animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.9, 0.5] }}
                  transition={{ repeat: Infinity, duration: 1.8, delay: i * 0.25 }}>⭐</motion.span>
              ))}
            </div>

            {/* Elegant Floral Burgundy Button */}
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 8px 30px rgba(157,23,77,0.25)", y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleClose}
              className="w-full py-3 rounded-2xl font-bold text-[12.5px] text-white flex items-center justify-center gap-1 shadow-md"
              style={{
                background: "linear-gradient(135deg, #BE123C 0%, #9F1239 100%)",
                letterSpacing: "0.02em",
              }}
            >
              Celebrate your special day with Sahvia! <span className="text-sm font-semibold ml-0.5">❯</span>
            </motion.button>

            <p className="text-stone-400 text-[8.5px] mt-2 font-semibold tracking-wide">This message will close automatically</p>
          </div>
        </motion.div>
      </motion.div>
    );
  } else {
    // ════════════════════════════════════════════════════════════════════════
    // MALE THEME (CYBERPUNK NEON / DARK MODE)
    // ════════════════════════════════════════════════════════════════════════
    return (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 z-[999] flex items-center justify-center p-4 select-none"
        style={{ background: "rgba(3, 2, 8, 0.72)", backdropFilter: "blur(18px)" }}
        onClick={handleClose}
      >
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {confetti.map(p => <ConfettiParticle key={p.id} {...p} />)}
        </div>

        <motion.div
          initial={{ scale: 0.72, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.85, opacity: 0, y: 15 }}
          transition={{ type: "spring", bounce: 0.32, duration: 0.9 }}
          onClick={e => e.stopPropagation()}
          className="relative w-full overflow-y-auto no-scrollbar"
          style={{
            maxWidth: "min(480px, calc(100vw - 2rem))",
            maxHeight: "92vh",
            background: "linear-gradient(145deg, #09061c 0%, #03020a 100%)",
            border: "1.5px solid rgba(99, 102, 241, 0.25)",
            borderRadius: "36px",
            boxShadow: "0 40px 100px rgba(139, 92, 246, 0.25), 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.1)",
          }}
        >
          {/* Background Radial Neon Lights */}
          <div className="absolute -top-16 left-1/4 w-72 h-44 pointer-events-none z-0"
            style={{ background: "radial-gradient(ellipse, rgba(168,85,247,0.2) 0%, transparent 70%)", filter: "blur(24px)" }} />
          <div className="absolute -bottom-16 right-1/4 w-72 h-44 pointer-events-none z-0"
            style={{ background: "radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)", filter: "blur(24px)" }} />

          {/* Glowing Animated Outer Border Ring */}
          <motion.div
            className="absolute inset-0 pointer-events-none z-0"
            animate={{ opacity: [0.3, 0.75, 0.3] }}
            transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
            style={{ border: "2px solid rgba(99, 102, 241, 0.35)", borderRadius: "36px" }}
          />

          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/12 text-white/50 hover:text-white border border-white/10 transition-all text-sm font-semibold"
          >✕</button>

          <div className="relative px-6 sm:px-9 pt-7 pb-6 text-center z-10">
            {/* Cyberpunk Neon Cake Illustration */}
            <div className="mb-3 relative">
              <svg className="mx-auto w-24 h-24 filter drop-shadow-[0_0_15px_rgba(139,92,246,0.6)]" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="cyber-cake-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#c084fc" />
                    <stop offset="50%" stopColor="#8b5cf6" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
                {/* Platform ring */}
                <ellipse cx="50" cy="82" rx="38" ry="11" fill="none" stroke="#6366f1" strokeWidth="2.5" opacity="0.9" />
                <ellipse cx="50" cy="82" rx="33" ry="9" fill="#110d29" stroke="#3b82f6" strokeWidth="1.5" />

                {/* Bottom tier */}
                <rect x="23" y="48" width="54" height="24" rx="4" fill="url(#cyber-cake-grad)" />
                {/* Glaze glow drip */}
                <path d="M23 48 Q30 54 36 48 Q43 54 50 48 Q57 54 64 48 Q71 54 77 48 L77 56 Q70 60 64 56 Q57 60 50 56 Q43 60 36 56 Q30 60 23 56 Z" fill="#e879f9" />
                <ellipse cx="50" cy="48" rx="27" ry="7.5" fill="#f472b6" />

                {/* Frosting dots */}
                <circle cx="32" cy="62" r="2.5" fill="#a5f3fc" />
                <circle cx="50" cy="65" r="2.5" fill="#fde047" />
                <circle cx="68" cy="62" r="2.5" fill="#a5f3fc" />

                {/* Candles */}
                <line x1="38" y1="34" x2="38" y2="48" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="50" y1="31" x2="50" y2="48" stroke="#e879f9" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="62" y1="34" x2="62" y2="48" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" />

                {/* Flames */}
                <circle cx="38" cy="30" r="3" fill="#fde047" className="animate-pulse" />
                <circle cx="50" cy="27" r="3.5" fill="#fdba74" className="animate-pulse" />
                <circle cx="62" cy="30" r="3" fill="#fde047" className="animate-pulse" />
              </svg>

              {/* Neon particles */}
              <div className="absolute top-4 left-14 text-sm text-cyan-400/80 animate-ping">🎈</div>
              <div className="absolute top-8 right-16 text-xs text-fuchsia-400/80 animate-ping delay-500">🎈</div>
            </div>

            <motion.h4
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="text-indigo-300 font-bold text-xs uppercase tracking-widest mb-1.5"
            >Dear</motion.h4>

            <motion.h2
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="font-extrabold text-white text-2xl sm:text-[1.85rem] tracking-tight mb-1.5"
            >{name},</motion.h2>

            <motion.h2
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className="font-black text-3xl sm:text-[2.35rem] tracking-tight leading-none mb-4"
              style={{
                background: "linear-gradient(90deg, #38BDF8 0%, #C084FC 50%, #EC4899 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}
            >
              Happy Birthday! <span className="inline-block text-lg align-middle translate-y-[-2px]">⚡</span>
            </motion.h2>

            {/* Glowing Banner Strip */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.45 }}
              className="mb-4 p-2.5 rounded-2xl flex items-center justify-center gap-2 border border-indigo-500/20 bg-indigo-950/20"
            >
              <span className="w-6 h-6 rounded-full bg-indigo-500/10 flex items-center justify-center text-xs">💜</span>
              <p className="text-indigo-200 text-[11px] font-semibold tracking-wide">
                Warm birthday wishes from all of us at <strong className="text-indigo-300 font-bold">Sahvia!</strong> 🌸✨
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
              className="text-slate-300 text-[12px] sm:text-[13px] leading-relaxed mb-4 font-medium text-center max-w-[350px] mx-auto space-y-2"
            >
              <p>
                Today, we celebrate not just your birthday, but also the positivity, dedication, and kindness you bring every day.
              </p>
              <p>
                Your efforts and spirit truly make a difference, and we’re grateful to have you as part of the <strong className="text-indigo-400">Sahvia</strong> family.
              </p>
            </motion.div>

            {/* Glowing Neon Quote Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.65 }}
              className="relative my-3.5 p-4 rounded-2xl border border-indigo-500/20 bg-indigo-950/15"
              style={{ boxShadow: "0 0 20px rgba(99,102,241,0.06) inset" }}
            >
              <div className="flex justify-center gap-6 text-indigo-400 text-lg mb-2">
                <span>🎂</span><span>🎁</span><span>🥂</span>
              </div>
              <p className="text-indigo-200 text-[12px] italic leading-relaxed px-2">
                “May this special year bring you happiness, success, good health, and countless beautiful moments to cherish.”
              </p>
            </motion.div>

            {/* Stars indicator */}
            <div className="flex justify-center gap-1.5 mb-4">
              {[...Array(5)].map((_, i) => (
                <motion.span key={i} className="text-xs"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.4, 1, 0.4] }}
                  transition={{ repeat: Infinity, duration: 1.6, delay: i * 0.25 }}>⭐</motion.span>
              ))}
            </div>

            {/* Glowing Cyber Neon Button */}
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(139,92,246,0.65)", y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleClose}
              className="w-full py-3 rounded-2xl font-extrabold text-[12.5px] text-white flex items-center justify-center gap-1 shadow-lg"
              style={{
                background: "linear-gradient(90deg, #EC4899 0%, #8B5CF6 50%, #3B82F6 100%)",
                letterSpacing: "0.03em",
              }}
            >
              Celebrate your special day with Sahvia! <span className="text-sm font-semibold ml-0.5">❯</span>
            </motion.button>

            <p className="text-white/20 text-[8.5px] mt-2 font-semibold tracking-wide">This message will close automatically</p>
          </div>
        </motion.div>
      </motion.div>
    );
  }
}

function useBirthdayPopup(profile) {
  const [show, setShow] = useState(false);
  const [partyOn, setPartyOn] = useState(false);

  useEffect(() => {
    if (!profile?.date_of_birth || !profile?.employee_id) return;
    if (!isTodayBirthday(profile.date_of_birth)) return;

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const partyKey = `bday_party_${profile.employee_id}_${todayStr}`;
    const seenKey = `bday_popup_${profile.employee_id}_${todayStr}`;

    // 1. Restore states
    if (localStorage.getItem(partyKey)) {
      setPartyOn(true);
    }
    if (localStorage.getItem(seenKey)) {
      setPartyOn(true);
    }

    // 2. Show the popup if not seen yet
    if (!localStorage.getItem(seenKey) && !localStorage.getItem(partyKey)) {
      const t = setTimeout(() => {
        setShow(true);
        localStorage.setItem(seenKey, "1");
      }, 800);
      return () => clearTimeout(t);
    }
  }, [profile?.date_of_birth, profile?.employee_id]);

  const close = () => setShow(false);

  const activateParty = useCallback(() => {
    setPartyOn(true);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    localStorage.setItem(`bday_party_${profile?.employee_id}_${todayStr}`, "1");
  }, [profile?.employee_id]);

  return { show, close, partyOn, activateParty };
}

// ════════════════════════════════════════════════════════════════════════════
// LEAVE CALENDAR (unchanged)
// ════════════════════════════════════════════════════════════════════════════
function useMonthlyLeaves(year, month) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawLeaves, setRaw] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getMonthlyLeaves(year, month + 1)
      .then(r => {
        const raw = (r.data?.leaves ?? []).filter(
          l => !l.leave_type?.toLowerCase().includes("medical")
        );
        setRaw(raw);
        const map = {};
        raw.forEach(l => {
          if (!map[l.employee_id]) {
            map[l.employee_id] = {
              employee_id: l.employee_id, full_name: l.full_name,
              photo: l.photo, designation: l.designation, department: l.department, ranges: [],
            };
          }
          map[l.employee_id].ranges.push({ leave_type: l.leave_type, start: l.start_date, end: l.end_date });
        });
        setRows(Object.values(map));
      })
      .catch(() => setError("Could not load leave calendar"))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { load(); }, [load]);
  return { rows, loading, error, refresh: load, rawLeaves };
}

function useDashboard(active) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!active) return;
    setLoading(true);
    setError(null);
    getDashboardSummary()
      .then(r => setData(r.data))
      .catch(() => setError("Could not load overview"))
      .finally(() => setLoading(false));
  }, [active]);

  useEffect(() => { if (active) load(); }, [active, load]);
  return { onLeave: data?.on_leave_today ?? [], birthdays: data?.upcoming_birthdays ?? [], loading, error, refresh: load };
}

// ─── Pending ticket count (admin only) ────────────────────────────────────────
function usePendingTicketCount(isAdmin) {
  const [counts, setCounts] = useState({ att: 0, leave: 0, other: 0 });

  const fetch = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await getEditTickets({ status: "PENDING" });
      const tickets = res.data.tickets || [];
      let att = 0, leave = 0, other = 0;
      tickets.forEach(t => {
        if (t.request_type === "ATTENDANCE") att++;
        else if (t.request_type === "LEAVE") leave++;
        else other++;
      });
      setCounts({ att, leave, other });
    } catch { setCounts({ att: 0, leave: 0, other: 0 }); }
  }, [isAdmin]);

  useEffect(() => {
    fetch();
    const id = setInterval(fetch, 60000);
    return () => clearInterval(id);
  }, [fetch]);

  return { counts, total: counts.att + counts.leave + counts.other, refresh: fetch };
}

// ─── Pending remote request count (admin & manager only) ──────────────────────
function usePendingRemoteCount(isEligible) {
  const [counts, setCounts] = useState({ remote: 0, wfh: 0 });

  const fetchCounts = useCallback(async () => {
    if (!isEligible) return;
    try {
      const [remoteRes, wfhRes] = await Promise.all([
        getRemoteAttendance({ status: "PENDING", include_all: "true" }),
        getWfhPeriods({ status: "PENDING" })
      ]);
      const remoteCount = remoteRes.data?.remote_requests?.length || 0;
      const wfhCount = wfhRes.data?.wfh_periods?.length || 0;
      setCounts({ remote: remoteCount, wfh: wfhCount });
    } catch (err) {
      console.error("Failed to fetch pending remote counts:", err);
      setCounts({ remote: 0, wfh: 0 });
    }
  }, [isEligible]);

  useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => clearInterval(id);
  }, [fetchCounts]);

  return {
    counts,
    total: counts.remote + counts.wfh,
    refresh: fetchCounts
  };
}

function downloadCSV(rawLeaves, year, month) {
  const monthName = MONTHS_LONG[month];
  const headers = ["Employee ID", "Full Name", "Designation", "Department", "Leave Type", "Start Date", "End Date", "Total Days", "Status"];
  const rows = rawLeaves.map(l => [
    l.employee_id, l.full_name, l.designation || "", l.department || "",
    l.leave_type, l.start_date, l.end_date, l.total_days, l.status || "APPROVED",
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `leave_report_${monthName}_${year}.csv`; a.click();
  URL.revokeObjectURL(url);
}

const LeaveCalendar = memo(function LeaveCalendar() {
  const today = new Date();
  const viewYear = today.getFullYear();
  const viewMonth = today.getMonth();
  const scrollRef = useRef(null);
  const { rows, loading, error, refresh, rawLeaves } = useMonthlyLeaves(viewYear, viewMonth);
  const totalDays = daysInMonth(viewYear, viewMonth);
  const days = useMemo(() => Array.from({ length: totalDays }, (_, i) => i + 1), [totalDays]);

  useEffect(() => {
    if (scrollRef.current && rows.length > 0) {
      setTimeout(() => {
        if (scrollRef.current) {
          const day = new Date().getDate();
          const containerW = scrollRef.current.clientWidth;
          const targetScroll = (day - 1) * 34 - containerW / 2 + 17;
          scrollRef.current.scrollLeft = Math.max(0, targetScroll);
        }
      }, 100);
    }
  }, [rows]);

  const isLeaveDay = useCallback((ranges, dayNum) => {
    const dow = new Date(viewYear, viewMonth, dayNum).getDay();
    if (dow === 0 || dow === 6) return null; // Weekends are week-offs, not leave days
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    for (const r of ranges) { if (iso >= r.start && iso <= r.end) return r; }
    return null;
  }, [viewYear, viewMonth]);

  const connInfo = useCallback((ranges, dayNum) => {
    const match = isLeaveDay(ranges, dayNum);
    if (!match) return null;
    const prev = isLeaveDay(ranges, dayNum - 1);
    const next = isLeaveDay(ranges, dayNum + 1);
    const samePrev = prev && prev.leave_type === match.leave_type && prev.start === match.start;
    const sameNext = next && next.leave_type === match.leave_type && next.end === match.end;
    return { leave: match, connLeft: !!samePrev, connRight: !!sameNext };
  }, [isLeaveDay]);

  const getDayOfWeek = (d) => new Date(viewYear, viewMonth, d).getDay();
  const isToday = (d) => viewYear === today.getFullYear() && viewMonth === today.getMonth() && d === today.getDate();
  const isWeekend = (d) => { const w = getDayOfWeek(d); return w === 0 || w === 6; };
  const CELL_W = 34;

  return (
    <motion.div variants={fadeUp} custom={3} initial="hidden" animate="visible">
      <GlassCard>
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-white/[0.06] flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-sky-500/15 border border-sky-500/20 flex items-center justify-center text-sm flex-shrink-0">📅</div>
            <div>
              <p className="text-white/85 font-bold text-[13px] leading-none">Leave Calendar</p>
              <p className="text-white/30 text-[11px] mt-0.5">{MONTHS_LONG[viewMonth]} {viewYear}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="hidden md:flex items-center gap-3 flex-wrap">
              {Object.entries(LEAVE_STYLES).filter(([k]) => k !== "Other").slice(0, 6).map(([type, s]) => (
                <div key={type} className="flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${s.cal} opacity-80 flex-shrink-0`} />
                  <span className="text-white/30 text-[10px] font-medium">{type}</span>
                </div>
              ))}
            </div>
            <motion.button
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => downloadCSV(rawLeaves, viewYear, viewMonth)}
              disabled={rawLeaves.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/25
                text-emerald-300 text-[11px] font-semibold transition-all
                disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <span>⬇</span> <span className="hidden xs:inline">Download</span> Report
            </motion.button>
          </div>
        </div>

        {loading ? <Spinner /> : error ? (
          <div className="flex items-center justify-center gap-2 py-6 text-white/30 text-sm flex-wrap px-4">
            <span>⚠</span>{error}
            <button onClick={refresh} className="underline opacity-60 hover:opacity-100">Retry</button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2.5">
            <span className="text-3xl opacity-40">🏖️</span>
            <p className="text-white/25 text-xs font-medium">No leaves this month</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="flex">
              {/* Sticky employee column */}
              <div className="flex-shrink-0 w-[130px] sm:w-[170px] border-r border-white/[0.05]">
                <div className="h-10 px-3 flex items-end pb-2">
                  <span className="text-white/25 text-[9px] font-bold uppercase tracking-widest">Employee</span>
                </div>
                {rows.map((emp, i) => (
                  <motion.div key={emp.employee_id} custom={i} variants={fadeUp} initial="hidden" animate="visible"
                    className="flex items-center gap-2 px-2 sm:px-3 py-2.5 border-t border-white/[0.04]"
                    style={{ height: 48 }}>
                    <Avatar name={emp.full_name} photo={normalizePhoto(emp.photo)} size={24} />
                    <div className="min-w-0">
                      <p className="text-white/80 text-[11px] sm:text-[12px] font-semibold truncate leading-none">{emp.full_name}</p>
                      <p className="text-white/30 text-[10px] truncate mt-0.5 hidden sm:block">{emp.employee_id}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Scrollable day columns */}
              <div className="flex-1 overflow-x-auto" ref={scrollRef}
                style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,.06) transparent", WebkitOverflowScrolling: "touch" }}>
                {/* Day header */}
                <div className="flex" style={{ minWidth: totalDays * CELL_W }}>
                  {days.map(d => {
                    const dow = getDayOfWeek(d);
                    const weekend = dow === 0 || dow === 6;
                    const mark = isToday(d);
                    return (
                      <div key={d} className={`flex flex-col items-center justify-end pb-1.5 flex-shrink-0 relative ${mark ? "bg-sky-500/10 rounded-t-xl" : ""}`} style={{ width: CELL_W, height: 40 }}>
                        <span className={`text-[8px] font-bold uppercase leading-none mb-0.5 ${mark ? "text-sky-400 animate-pulse" : weekend ? "text-white/20" : "text-white/25"}`}>{DAY_LABELS[dow]}</span>
                        <span className={`text-[10px] font-bold leading-none ${mark ? "text-white bg-sky-500 w-6 h-6 rounded-full flex items-center justify-center ring-2 ring-sky-400 ring-offset-1 ring-offset-slate-900 shadow-[0_0_10px_rgba(56,189,248,0.5)]" : weekend ? "text-white/25" : "text-white/45"}`}>{d}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Employee rows */}
                {rows.map(emp => (
                  <div key={emp.employee_id} className="flex border-t border-white/[0.04] items-center" style={{ minWidth: totalDays * CELL_W, height: 48 }}>
                    {days.map(d => {
                      const info = connInfo(emp.ranges, d);
                      const weekend = isWeekend(d);
                      const todayD = isToday(d);

                      if (!info) {
                        return (
                          <div key={d} className={`flex-shrink-0 flex items-center justify-center relative ${todayD ? "bg-sky-500/10" : ""} ${weekend ? "opacity-30" : ""}`} style={{ width: CELL_W, height: 48 }}>
                            {weekend && <div className="w-1 h-3 rounded-full bg-emerald-400/30" />}
                            {todayD && !weekend && <div className="w-0.5 h-6 rounded-full bg-sky-400 shadow-[0_0_8px_#38bdf8] animate-pulse" />}
                          </div>
                        );
                      }

                      const style = leaveStyle(info.leave.leave_type);
                      const isFirst = !info.connLeft;
                      const isLast = !info.connRight;
                      const isOnly = isFirst && isLast;

                      return (
                        <div key={d} className={`flex-shrink-0 flex items-center relative ${todayD ? "bg-sky-500/10" : ""}`} style={{ width: CELL_W, height: 48 }}>
                          {!isLast && <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-[26px] ${style.cal} opacity-70 z-0`} />}
                          {!isFirst && <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1/2 h-[26px] ${style.cal} opacity-70 z-0`} />}
                          <div className={`relative z-10 mx-auto w-[26px] h-[26px] flex items-center justify-center font-bold text-[11px] text-white shadow-md
                            ${isOnly ? "rounded-full" : isFirst ? "rounded-l-full" : isLast ? "rounded-r-full" : "rounded-none"}
                            ${style.cal} opacity-90`}>
                            {(isFirst || isOnly) && <span className="tabular-nums select-none">{d}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile legend */}
            <div className="md:hidden flex flex-wrap gap-3 px-4 py-3 border-t border-white/[0.05]">
              {Object.entries(LEAVE_STYLES).filter(([k]) => k !== "Other").map(([type, s]) => (
                <div key={type} className="flex items-center gap-1.5">
                  <span className={`w-2.5 h-2.5 rounded-full ${s.cal} opacity-80 flex-shrink-0`} />
                  <span className="text-white/30 text-[10px]">{type}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
});

// ════════════════════════════════════════════════════════════════════════════
// PROFILE HERO (for modal popup)
// ════════════════════════════════════════════════════════════════════════════
const ProfileHero = memo(function ProfileHero({ profile, onEdit, editable, activeTab, partyOn, isSuperAdmin }) {
  const meta = ROLE_META[profile.role] || ROLE_META.EMPLOYEE;
  const showEdit = editable && activeTab === "profile";
  const isProfileBirthday = isTodayBirthday(profile.date_of_birth);

  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible"
      className="flex items-start sm:items-center justify-between mb-5 flex-wrap gap-3 border-b border-white/[0.06] pb-5">
      <div className="flex items-center gap-3 sm:gap-4 min-w-0">
        <div className="relative flex-shrink-0">
          <Avatar name={profile.full_name} photo={null} size={48} />
          <span className={`absolute -bottom-1 -right-1 w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full border-2 border-black/40 ${meta.dot}`} />
          {partyOn && isProfileBirthday && (
            <motion.span className="absolute -top-2 -right-2 text-base sm:text-lg"
              animate={{ rotate: [-10, 10, -10], scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}>🎂</motion.span>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-[1.55rem] font-black text-white tracking-tight leading-none truncate max-w-[180px] sm:max-w-none">
              {profile.full_name || "—"}
            </h1>
            {profile.role === "ADMIN" ? (
              isSuperAdmin ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 bg-amber-500/20 text-amber-300 border-amber-500/30">
                  👑 Super Admin
                </span>
              ) : (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.color}`}>
                  {meta.label}
                </span>
              )
            ) : (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${meta.color}`}>
                {meta.label}
              </span>
            )}
            {partyOn && isProfileBirthday && (
              <motion.span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-amber-500/20 text-amber-300 border-amber-500/30 flex-shrink-0"
                animate={{ opacity: [0.7, 1, 0.7] }} transition={{ repeat: Infinity, duration: 1.8 }}>🎉 Birthday!</motion.span>
            )}
          </div>
          <p className="text-white/35 text-[11px] sm:text-xs mt-1 font-medium truncate">
            {[profile.designation, profile.department, profile.employee_id].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
      {showEdit && (
        <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onEdit}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm font-semibold
            bg-white/[0.07] hover:bg-white/[0.12] border border-white/[0.1] text-white/70 hover:text-white
            transition-all active:scale-95 flex-shrink-0">
          <span>✏️</span>
          <span className="hidden xs:inline">Edit Profile</span>
        </motion.button>
      )}
    </motion.div>
  );
});

// ════════════════════════════════════════════════════════════════════════════
// PROFILE FORM (unchanged)
// ════════════════════════════════════════════════════════════════════════════
const ProfileForm = memo(function ProfileForm({ profile, editable, isEditing, onChange }) {
  return (
    <motion.div variants={fadeIn} initial="hidden" animate="visible"
      className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <GlassCard className="p-4 sm:p-5">
        <div className="flex items-center gap-2.5 mb-4 sm:mb-5">
          <div className="w-7 h-7 rounded-lg bg-blue-500/15 border border-blue-500/20 flex items-center justify-center text-xs flex-shrink-0">👤</div>
          <h3 className="text-white/80 font-bold text-sm">Personal</h3>
        </div>
        <div className="space-y-3">
          <FormField label="Employee ID" value={profile.employee_id} icon="🆔" />
          <FormField label="Full Name" value={profile.full_name} icon="👤" editable={editable && isEditing} onChange={v => onChange("full_name", v)} />
          <FormField label="Email" value={profile.email} icon="📧" editable={editable && isEditing} onChange={v => onChange("email", v)} />
          <FormField label="Date of Birth" value={profile.date_of_birth} icon="🎂" editable={editable && isEditing} onChange={v => onChange("date_of_birth", v)} />
          <FormField label="Mobile" value={profile.mobile_number} icon="📱" editable={editable && isEditing} onChange={v => onChange("mobile_number", v)} />
        </div>
      </GlassCard>

      <GlassCard className="p-4 sm:p-5">
        <div className="flex items-center gap-2.5 mb-4 sm:mb-5">
          <div className="w-7 h-7 rounded-lg bg-violet-500/15 border border-violet-500/20 flex items-center justify-center text-xs flex-shrink-0">💼</div>
          <h3 className="text-white/80 font-bold text-sm">Professional</h3>
        </div>
        <div className="space-y-3">
          <FormField label="Department" value={profile.department} icon="🏢" editable={editable && isEditing} onChange={v => onChange("department", v)} />
          <FormField label="Designation" value={profile.designation} icon="💼" editable={editable && isEditing} onChange={v => onChange("designation", v)} />
          <FormField label="Manager" value={profile.manager_display || "—"} icon="👔" />
          <FormField label="Location" value={profile.location || "—"} icon="📍" />
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-white/[0.06]">
            <span className="text-white/40 text-xs font-semibold uppercase tracking-wider">Status</span>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/15">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
              <span className="text-xs font-bold text-emerald-400">{profile.status}</span>
            </div>
          </div>
        </div>
      </GlassCard>
    </motion.div>
  );
});

function FormField({ label, value, icon, editable, onChange }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/30 mb-1.5">
        <span className="text-[11px] flex-shrink-0">{icon}</span>{label}
      </label>
      <input
        value={value || ""} disabled={!editable}
        onChange={e => onChange && onChange(e.target.value)}
        className={`w-full px-3 sm:px-3.5 py-2 rounded-xl text-sm font-medium text-white/85 outline-none transition-all
          ${editable
            ? "bg-white/[0.08] border border-blue-400/40 focus:border-blue-400/70 focus:bg-white/[0.1]"
            : "bg-white/[0.04] border border-white/[0.06] cursor-default select-none"}`}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// WORKFORCE PANELS (unchanged)
// ════════════════════════════════════════════════════════════════════════════


function OnLeaveTodayCard({ onLeave, loading, onViewAll }) {
  return (
    <motion.div variants={fadeUp} custom={2} initial="hidden" animate="visible" className="flex flex-col h-full">
      <GlassCard className="p-4 flex flex-col justify-between h-full bg-white/[0.03] backdrop-blur-[25px] border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-base text-rose-400">🚫</span>
            <span className="text-white/80 font-bold text-xs sm:text-sm tracking-wide">On Leave Today</span>
          </div>
          <button onClick={onViewAll} className="text-[10px] text-blue-400 hover:text-blue-300 font-bold transition-colors">View All</button>
        </div>
        {loading ? <Spinner /> : onLeave.length === 0 ? (
          <div className="flex flex-col items-center py-2.5 gap-1.5">
            <span className="text-xl opacity-40">✨</span>
            <p className="text-white/35 text-[10px] font-medium">Full house today!</p>
          </div>
        ) : onLeave.length === 1 ? (
          <div className="flex items-center gap-3 mt-1 w-full">
            <Avatar name={onLeave[0].full_name} photo={normalizePhoto(onLeave[0].photo)} size={32} />
            <div className="flex flex-col">
              <span className="text-white font-bold text-xs sm:text-sm">{onLeave[0].full_name}</span>
              <span className="text-white/40 text-[10px]">{onLeave[0].employee_id}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
            <div className="flex -space-x-2.5 overflow-hidden">
              {onLeave.slice(0, 4).map((e, i) => (
                <div key={e.employee_id} style={{ zIndex: 10 - i }}>
                  <Avatar name={e.full_name} photo={normalizePhoto(e.photo)} size={28} />
                </div>
              ))}
              {onLeave.length > 4 && (
                <div className="w-7 h-7 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/70 text-[9px] font-black z-20">
                  +{onLeave.length - 4}
                </div>
              )}
            </div>
            <span className="text-white/45 text-[10px] font-semibold tabular-nums ml-auto">
              {onLeave.length} Employees
            </span>
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
}

function TodayBirthdaysCard({ birthdays, loading, onViewAll }) {
  const upcomingBdays = birthdays.filter(b => b.days_away >= 0 && b.days_away <= 3);
  return (
    <motion.div variants={fadeUp} custom={2} initial="hidden" animate="visible" className="flex flex-col h-full">
      <GlassCard className="p-4 flex flex-col justify-between h-full bg-white/[0.03] backdrop-blur-[25px] border border-white/[0.08] shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-base text-amber-400">🎂</span>
            <span className="text-white/80 font-bold text-xs sm:text-sm tracking-wide">Celebrating Birthdays</span>
          </div>
          <button onClick={onViewAll} className="text-[10px] text-blue-400 hover:text-blue-300 font-bold transition-colors">View All</button>
        </div>
        {loading ? <Spinner /> : upcomingBdays.length === 0 ? (
          <div className="flex flex-col items-center py-2.5 gap-1.5">
            <span className="text-xl opacity-40">🌙</span>
            <p className="text-white/35 text-[10px] font-medium">No birthdays soon</p>
          </div>
        ) : upcomingBdays.length === 1 ? (
          <div className="flex items-center gap-3 mt-1 w-full">
            <Avatar name={upcomingBdays[0].full_name} photo={normalizePhoto(upcomingBdays[0].photo)} size={32} />
            <div className="flex flex-col">
              <span className="text-white font-bold text-xs sm:text-sm">{upcomingBdays[0].full_name}</span>
              <span className="text-white/40 text-[10px]">
                {upcomingBdays[0].employee_id} • <span className="text-amber-300 font-semibold">{upcomingBdays[0].label}</span>
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
            <div className="flex -space-x-2.5 overflow-hidden">
              {upcomingBdays.slice(0, 4).map((e, i) => (
                <div key={e.employee_id} style={{ zIndex: 10 - i }}>
                  <Avatar name={e.full_name} photo={normalizePhoto(e.photo)} size={28} />
                </div>
              ))}
              {upcomingBdays.length > 4 && (
                <div className="w-7 h-7 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white/70 text-[9px] font-black z-20">
                  +{upcomingBdays.length - 4}
                </div>
              )}
            </div>
            <span className="text-white/45 text-[10px] font-semibold tabular-nums ml-auto">
              {upcomingBdays.length} Employees
            </span>
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
}
// ════════════════════════════════════════════════════════════════════════════
// PENDING TICKETS MODAL (ADMIN ONLY)
// ════════════════════════════════════════════════════════════════════════════
const TICKET_TABS = [
  { key: "ATTENDANCE", label: "Attendance", icon: "📊", color: "indigo" },
  { key: "LEAVE", label: "Leave", icon: "🏖️", color: "rose" },
  { key: "PROFILE_UPDATE", label: "Other Issues", icon: "🔧", color: "amber" },
];

const TAB_COLORS = {
  indigo: { active: "bg-indigo-500/30 border-indigo-400/50 text-indigo-200", dot: "bg-indigo-400" },
  rose: { active: "bg-rose-500/30 border-rose-400/50 text-rose-200", dot: "bg-rose-400" },
  amber: { active: "bg-amber-500/30 border-amber-400/50 text-amber-200", dot: "bg-amber-400" },
};

// ── Helpers matching AttendanceReport.jsx & LeaveReport.jsx ───────────────────
const ATTENDANCE_STATUSES = ["PRESENT", "ABSENT", "HALF_DAY", "WORK_FROM_HOME", "HOLIDAY", "WEEK_OFF", "ON_LEAVE"];

function parseISOAsLocal(isoStr) {
  if (!isoStr) return null;
  const local = isoStr.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
  return new Date(local);
}

function fmtTimeFromISO(isoStr) {
  if (!isoStr) return "—";
  const d = parseISOAsLocal(isoStr);
  if (!d || isNaN(d)) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function isoToDateTimeLocal(isoStr) {
  if (!isoStr) return "";
  const d = parseISOAsLocal(isoStr);
  if (!d || isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function dateTimeLocalToUTC(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  if (isNaN(d)) return null;
  return d.toISOString();
}

function statusColor(s) {
  switch (s) {
    case "PRESENT": return "bg-emerald-500/20 text-emerald-300";
    case "ABSENT": return "bg-red-500/20 text-red-300";
    case "HOLIDAY": return "bg-purple-500/20 text-purple-300";
    case "WEEK_OFF": return "bg-indigo-500/20 text-indigo-300";
    case "ON_LEAVE": return "bg-orange-500/20 text-orange-300";
    case "HALF_DAY": return "bg-yellow-500/20 text-yellow-300";
    case "WORK_FROM_HOME": return "bg-cyan-500/20 text-cyan-300";
    default: return "bg-slate-500/20 text-slate-300";
  }
}

const LEAVE_TYPES = [
  { value: "CASUAL", label: "Casual Leave", singleDay: false, isCL: true },
  { value: "HALF CASUAL", label: "Half Day Casual", singleDay: true, isCL: true },
  { value: "SICK", label: "Sick Leave", singleDay: false, isCL: false },
  { value: "HALF DAY", label: "Half Day", singleDay: true, isCL: false },
  { value: "OTHER", label: "Other", singleDay: false, isCL: false },
  { value: "COMP_OFF", label: "Comp Off", singleDay: true, isCL: false },
];
const LEAVE_STATUSES = ["Pending", "Approved", "Rejected"];
const SINGLE_DAY_TYPES = new Set(["HALF CASUAL", "HALF DAY", "COMP_OFF"]);

function mapDBStatusToDisplay(status) {
  if (!status) return "Pending";
  const s = status.toUpperCase();
  if (s === "APPROVED") return "Approved";
  if (s === "REJECTED") return "Rejected";
  return "Pending";
}

function StatusBadge({ status }) {
  const styles = {
    Approved: "bg-emerald-500/30 text-emerald-200 border-emerald-400/30",
    Rejected: "bg-red-500/30 text-red-200 border-red-400/30",
    Pending: "bg-yellow-500/30 text-yellow-200 border-yellow-400/30",
  };
  const normalized = mapDBStatusToDisplay(status);
  return (
    <span className={`px-2 py-0.5 sm:py-1 rounded-lg text-xs border font-semibold ${styles[normalized] || styles.Pending}`}>
      {normalized}
    </span>
  );
}

function validateEditForm(form, leaveTypes = LEAVE_TYPES) {
  const { leave_type, leave_date_from, leave_date_to } = form;
  if (!leave_type) return "Leave type is required.";
  if (!leave_date_from) return "Leave To date is required.";
  if (!leave_date_to) return "Leave To date is required.";
  if (leave_date_to < leave_date_from) return "Leave To date cannot be before Leave From.";
  if (SINGLE_DAY_TYPES.has(leave_type) && leave_date_from !== leave_date_to) {
    const matched = leaveTypes.find(t => t.value === leave_type);
    return `"${matched?.label ?? matched?.name ?? leave_type}" must be a single day.`;
  }
  return null;
}



const ClearableTimeInput = ({ value, onChange, onClear }) => {
  // Parse YYYY-MM-DDTHH:MM or HH:MM
  let hourVal = "";
  let minVal = "";
  let periodVal = "AM";

  if (value) {
    const timePart = value.includes("T") ? value.split("T")[1] : value;
    if (timePart) {
      const [hStr, mStr] = timePart.split(":");
      const h24 = parseInt(hStr || "0", 10);
      minVal = mStr ? mStr.slice(0, 2) : "00";
      periodVal = h24 >= 12 ? "PM" : "AM";
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
      hourVal = String(h12).padStart(2, "0");
    }
  }

  const handleHourChange = (newHour) => {
    if (!newHour) {
      onClear();
      return;
    }
    const h12 = parseInt(newHour, 10);
    const m = minVal || "00";
    const p = periodVal || "AM";
    let h24 = h12;
    if (p === "PM" && h12 < 12) h24 += 12;
    if (p === "AM" && h12 === 12) h24 = 0;

    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  };

  const handleMinChange = (newMin) => {
    const h = hourVal || "09";
    const h12 = parseInt(h, 10);
    const m = newMin || "00";
    const p = periodVal || "AM";
    let h24 = h12;
    if (p === "PM" && h12 < 12) h24 += 12;
    if (p === "AM" && h12 === 12) h24 = 0;

    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  };

  const handlePeriodChange = (newPeriod) => {
    const h = hourVal || "09";
    const h12 = parseInt(h, 10);
    const m = minVal || "00";
    const p = newPeriod;
    let h24 = h12;
    if (p === "PM" && h12 < 12) h24 += 12;
    if (p === "AM" && h12 === 12) h24 = 0;

    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  };

  const hoursList = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutesList = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  return (
    <div className="flex items-center gap-1 bg-white/5 border border-cyan-500/20 rounded-xl p-1.5 min-w-[195px] shadow-lg backdrop-blur-md">
      {/* Hour Select */}
      <select
        value={hourVal}
        onChange={(e) => handleHourChange(e.target.value)}
        className="bg-slate-900 border border-white/10 rounded px-1.5 py-0.5 text-white text-[11px] font-bold outline-none focus:border-cyan-400 cursor-pointer"
      >
        <option value="" className="text-white/40">HH</option>
        {hoursList.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>

      <span className="text-cyan-400 font-bold text-xs">:</span>

      {/* Minute Select */}
      <select
        value={minVal}
        onChange={(e) => handleMinChange(e.target.value)}
        disabled={!hourVal}
        className="bg-slate-900 border border-white/10 rounded px-1.5 py-0.5 text-white text-[11px] font-bold outline-none focus:border-cyan-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <option value="">MM</option>
        {minutesList.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>

      {/* AM/PM Select */}
      <select
        value={periodVal}
        onChange={(e) => handlePeriodChange(e.target.value)}
        disabled={!hourVal}
        className="bg-slate-900 border border-white/10 rounded px-1.5 py-0.5 text-white text-[11px] font-bold outline-none focus:border-cyan-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>

      {/* Clear Button */}
      {value ? (
        <button
          type="button"
          onClick={onClear}
          title="Clear time"
          className="ml-auto w-5 h-5 rounded-lg bg-rose-500/20 hover:bg-rose-500/40 text-rose-300 hover:text-rose-100 text-[10px] font-bold transition flex items-center justify-center border border-rose-500/30"
        >
          ✕
        </button>
      ) : (
        <span className="ml-auto w-5 h-5 rounded bg-white/5 text-white/20 text-[10px] flex items-center justify-center">–</span>
      )}
    </div>
  );
};

const EMPTY_OBJ = {};

// ── Attendance inline edit row ────────────────────────────────────────────────
function AttendanceTicketRow({ tk, onResolved, isEditing, onStartEdit, onCancelEdit, note, setNote, resolving, onReject }) {
  const rc = tk.requested_changes || EMPTY_OBJ;
  const rd = tk.reference_details || EMPTY_OBJ;
  const attendance_date = rd.attendance_date || rc.attendance_date || "";

  // For editing state inside the row
  const [form, setForm] = useState({
    status: rd.status || "PRESENT",
    check_in: rd.check_in ? isoToDateTimeLocal(rd.check_in) : null,
    check_out: rd.check_out ? isoToDateTimeLocal(rd.check_out) : null,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isEditing) {
      setForm({
        status: rd.status || "PRESENT",
        check_in: rd.check_in ? isoToDateTimeLocal(rd.check_in) : null,
        check_out: rd.check_out ? isoToDateTimeLocal(rd.check_out) : null,
      });
      setErr("");
    }
  }, [isEditing, rd]);

  const handleCheckInChange = (timeStr) => {
    if (!timeStr) {
      setForm(f => ({ ...f, check_in: null }));
      return;
    }
    setForm(f => ({ ...f, check_in: `${attendance_date}T${timeStr}` }));
  };

  const handleCheckOutChange = (timeStr) => {
    if (!timeStr) {
      setForm(f => ({ ...f, check_out: null }));
      return;
    }
    setForm(f => ({ ...f, check_out: `${attendance_date}T${timeStr}` }));
  };

  const handleSave = async () => {
    setSaving(true);
    setErr("");
    try {
      const payload = { status: form.status };
      if (form.check_in === null) payload.check_in = null;
      else if (form.check_in) payload.check_in = dateTimeLocalToUTC(form.check_in);
      if (form.check_out === null) payload.check_out = null;
      else if (form.check_out) payload.check_out = dateTimeLocalToUTC(form.check_out);
      await editAttendance(tk.reference_id, payload);
      onResolved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const formattedDate = (rd.attendance_date || rc.attendance_date)
    ? new Date((rd.attendance_date || rc.attendance_date) + "T00:00:00").toLocaleDateString("en-GB")
    : "—";

  return (
    <tr className={`border-b border-white/5 transition ${isEditing ? "bg-cyan-500/10 ring-1 ring-inset ring-cyan-400/40" : "hover:bg-white/5"}`}>
      <td className="py-3 px-3 text-left">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-xs text-white bg-gradient-to-br from-indigo-500 to-violet-600 flex-shrink-0">
            {(tk.employee_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-[12px] truncate">{tk.employee_name || "Unknown"}</p>
            <p className="text-white/30 text-[10px] truncate">{tk.employee_id}</p>
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-white font-medium whitespace-nowrap">
        {formattedDate}
      </td>
      <td className="px-2 py-3">
        {isEditing ? (
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            className="bg-slate-800 border border-cyan-400/50 rounded px-1.5 py-1 text-white text-xs focus:outline-none w-full min-w-[100px]"
          >
            {ATTENDANCE_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        ) : (
          <span className={`px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-bold ${statusColor(rc.status || rd.status || "PRESENT")}`}>
            {rc.status || rd.status || "PRESENT"}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-white/80 whitespace-nowrap">
        {isEditing ? (
          <ClearableTimeInput
            value={form.check_in}
            onChange={handleCheckInChange}
            onClear={() => setForm((f) => ({ ...f, check_in: null }))}
          />
        ) : (
          <span className={(rc.check_in || rd.check_in) ? "text-xs" : "text-white/30 italic text-[10px]"}>
            {(rc.check_in || rd.check_in) ? fmtTimeFromISO(rc.check_in || rd.check_in) : "—"}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-white/80 whitespace-nowrap">
        {isEditing ? (
          <ClearableTimeInput
            value={form.check_out}
            onChange={handleCheckOutChange}
            onClear={() => setForm((f) => ({ ...f, check_out: null }))}
          />
        ) : (
          <span className={(rc.check_out || rd.check_out) ? "text-xs" : "text-white/30 italic text-[10px]"}>
            {(rc.check_out || rd.check_out) ? fmtTimeFromISO(rc.check_out || rd.check_out) : "—"}
          </span>
        )}
      </td>
      <td className="px-2 py-3 text-left text-white/70 italic max-w-[150px] break-words">
        {tk.reason || "—"}
      </td>
      <td className="px-2 py-3 whitespace-nowrap">
        {err && <p className="text-red-400 text-[10px] mb-1 whitespace-normal max-w-[150px]">{err}</p>}
        <div className="flex flex-col gap-1.5 min-w-[160px] max-w-[180px] mx-auto">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Admin note (optional)"
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-[11px] focus:outline-none focus:border-indigo-500 w-full"
          />
          <div className="flex gap-1 w-full">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[11px] font-bold transition"
                >
                  {saving ? "…" : "✅ Approve"}
                </button>
                <button
                  onClick={onCancelEdit}
                  disabled={saving}
                  className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold transition"
                >
                  ✕
                </button>
              </>
            ) : (
              <button
                onClick={onStartEdit}
                className="flex-1 py-1 rounded bg-yellow-500 hover:bg-yellow-400 text-slate-900 text-[11px] font-bold transition"
              >
                ✏️ Edit
              </button>
            )}
            <button
              onClick={() => onReject(note)}
              disabled={saving || resolving}
              className="px-2 py-1 rounded bg-red-600/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 text-[11px] font-bold transition"
            >
              {resolving === "reject" ? "…" : "✕ Reject"}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Leave inline edit row ─────────────────────────────────────────────────────
function LeaveTicketRow({ tk, onResolved, isEditing, onStartEdit, onCancelEdit, note, setNote, resolving, onReject, leaveTypes = LEAVE_TYPES }) {
  const rc = tk.requested_changes || EMPTY_OBJ;
  const rd = tk.reference_details || EMPTY_OBJ;

  // For editing state inside the row
  const [form, setForm] = useState({
    leave_date_from: rd.leave_date_from || "",
    leave_date_to: rd.leave_date_to || "",
    leave_type: rd.leave_type || "CASUAL",
    reason: rd.reason || tk.reason || "",
    status: mapDBStatusToDisplay(rd.status),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isSingle = SINGLE_DAY_TYPES.has(form.leave_type);

  useEffect(() => {
    if (isEditing) {
      setForm({
        leave_date_from: rd.leave_date_from || "",
        leave_date_to: rd.leave_date_to || "",
        leave_type: rd.leave_type || "CASUAL",
        reason: rd.reason || tk.reason || "",
        status: mapDBStatusToDisplay(rd.status),
      });
      setErr("");
    }
  }, [isEditing, rd, tk.reason]);

  const handleTypeChange = (newType) => {
    setForm((prev) => ({
      ...prev,
      leave_type: newType,
      leave_date_to: SINGLE_DAY_TYPES.has(newType) ? prev.leave_date_from : prev.leave_date_to,
    }));
  };

  const handleFromChange = (newFrom) => {
    setForm((prev) => {
      const isSingleType = SINGLE_DAY_TYPES.has(prev.leave_type);
      const newTo = isSingleType ? newFrom : prev.leave_date_to < newFrom ? newFrom : prev.leave_date_to;
      return { ...prev, leave_date_from: newFrom, leave_date_to: newTo };
    });
  };

  const handleSave = async () => {
    const valErr = validateEditForm(form, leaveTypes);
    if (valErr) {
      setErr(valErr);
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await editLeave(tk.reference_id, {
        leave_date_from: form.leave_date_from,
        leave_date_to: form.leave_date_to,
        leave_type: form.leave_type,
        reason: form.reason,
        status: form.status,
      });
      onResolved();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const fromDateStr = rc.leave_date_from || rd.leave_date_from;
  const toDateStr = rc.leave_date_to || rd.leave_date_to;
  const dateText = fromDateStr === toDateStr
    ? new Date(fromDateStr + "T00:00:00").toLocaleDateString("en-GB")
    : `${new Date(fromDateStr + "T00:00:00").toLocaleDateString("en-GB")} – ${new Date(toDateStr + "T00:00:00").toLocaleDateString("en-GB")}`;

  const leaveTypeRaw = rc.leave_type || rd.leave_type || "";
  const leaveTypeUpper = typeof leaveTypeRaw === "string" ? leaveTypeRaw.toUpperCase() : leaveTypeRaw;
  const matchedType = leaveTypes.find(t => t.value === leaveTypeUpper);
  const leaveTypeLabel = matchedType ? matchedType.label : leaveTypeRaw;

  return (
    <tr className={`border-b border-white/5 transition ${isEditing ? "bg-rose-500/10 ring-1 ring-inset ring-rose-400/40" : "hover:bg-white/5"}`}>
      <td className="py-3 px-3 text-left">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-xs text-white bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0">
            {(tk.employee_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-[12px] truncate">{tk.employee_name || "Unknown"}</p>
            <p className="text-white/30 text-[10px] truncate">{tk.employee_id}</p>
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-white/80 whitespace-nowrap text-xs">
        {isEditing ? (
          <div className="flex flex-col gap-1 text-left min-w-[120px]">
            <input
              type="date"
              value={form.leave_date_from || ""}
              onChange={(e) => handleFromChange(e.target.value)}
              className="bg-white/5 border border-rose-400/50 rounded px-1.5 py-0.5 text-white text-xs w-full focus:outline-none focus:border-rose-400"
            />
            <input
              type="date"
              value={form.leave_date_to || ""}
              min={form.leave_date_from || ""}
              disabled={isSingle}
              onChange={(e) => setForm((f) => ({ ...f, leave_date_to: e.target.value }))}
              className={`bg-white/5 border border-rose-400/50 rounded px-1.5 py-0.5 text-white text-xs w-full focus:outline-none focus:border-rose-400 ${isSingle ? "opacity-40 cursor-not-allowed" : ""}`}
            />
          </div>
        ) : (
          dateText
        )}
      </td>
      <td className="px-2 py-3">
        {isEditing ? (
          <select
            value={form.leave_type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="bg-slate-800 border border-rose-400/50 rounded px-1.5 py-1 text-white text-xs focus:outline-none w-full min-w-[100px]"
          >
            {leaveTypes.map((lt) => (
              <option key={lt.value} value={lt.value}>{lt.label}</option>
            ))}
          </select>
        ) : (
          <span className="px-2 py-0.5 sm:py-1 rounded-lg bg-blue-500/20 text-blue-200 border border-blue-400/20 text-[10px]">
            {leaveTypeLabel}
          </span>
        )}
      </td>
      <td className="px-2 py-3">
        {isEditing ? (
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            className="bg-slate-800 border border-rose-400/50 rounded px-1.5 py-1 text-white text-xs focus:outline-none w-full min-w-[90px]"
          >
            {LEAVE_STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        ) : (
          <StatusBadge status={rc.status || rd.status || "Pending"} />
        )}
      </td>
      <td className="px-2 py-3 text-left text-white/70 italic max-w-[150px] break-words">
        {isEditing ? (
          <input
            type="text"
            value={form.reason || ""}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
            className="bg-white/5 border border-rose-400/50 rounded px-1.5 py-0.5 text-white text-xs w-full focus:outline-none focus:border-rose-400"
            placeholder="Reason"
          />
        ) : (
          tk.reason || "—"
        )}
      </td>
      <td className="px-2 py-3 whitespace-nowrap">
        {err && <p className="text-red-400 text-[10px] mb-1 whitespace-normal max-w-[150px]">{err}</p>}
        <div className="flex flex-col gap-1.5 min-w-[160px] max-w-[180px] mx-auto">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Admin note (optional)"
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-[11px] focus:outline-none focus:border-indigo-500 w-full"
          />
          <div className="flex gap-1 w-full">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[11px] font-bold transition"
                >
                  {saving ? "…" : "✅ Approve"}
                </button>
                <button
                  onClick={onCancelEdit}
                  disabled={saving}
                  className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-white text-[11px] font-semibold transition"
                >
                  ✕
                </button>
              </>
            ) : (
              <button
                onClick={onStartEdit}
                className="flex-1 py-1 rounded bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-500/30 text-indigo-300 text-[11px] font-bold transition"
              >
                ✏️ Edit & Approve
              </button>
            )}
            <button
              onClick={() => onReject(note)}
              disabled={saving || resolving}
              className="px-2 py-1 rounded bg-red-600/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 text-[11px] font-bold transition"
            >
              {resolving === "reject" ? "…" : "✕ Reject"}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Profile Update Row ────────────────────────────────────────────────────────
function ProfileUpdateTicketRow({ tk, onResolved, note, setNote, resolving, onReject, onApprove }) {
  const rc = tk.requested_changes || {};

  return (
    <tr className="border-b border-white/5 hover:bg-white/5 transition">
      <td className="py-3 px-3 text-left">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-xs text-white bg-gradient-to-br from-amber-500 to-orange-600 flex-shrink-0">
            {(tk.employee_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-[12px] truncate">{tk.employee_name || "Unknown"}</p>
            <p className="text-white/30 text-[10px] truncate">{tk.employee_id}</p>
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-white/80 whitespace-nowrap text-xs">
        {tk.created_at ? new Date(tk.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
      </td>
      <td className="px-2 py-3 text-left">
        <div className="grid grid-cols-1 gap-1 max-w-[280px]">
          {Object.entries(rc).map(([field, val]) => (
            <div key={field} className="text-white/70 text-[11px] leading-tight">
              <span className="text-white/40 font-semibold capitalize">{field.replace(/_/g, " ")}:</span>{" "}
              <span className="text-amber-400 font-medium break-all">{String(val)}</span>
            </div>
          ))}
        </div>
      </td>
      <td className="px-2 py-3 whitespace-nowrap">
        <div className="flex flex-col gap-1.5 min-w-[160px] max-w-[180px] mx-auto">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Admin note (optional)"
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white text-[11px] focus:outline-none focus:border-indigo-500 w-full"
          />
          <div className="flex gap-1.5 w-full">
            <button
              onClick={() => onApprove(note)}
              disabled={resolving}
              className="flex-1 py-1 rounded bg-emerald-600/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold transition flex items-center justify-center gap-1"
            >
              {resolving === "approve" ? "…" : "✅ Approve"}
            </button>
            <button
              onClick={() => onReject(note)}
              disabled={resolving}
              className="flex-1 py-1 rounded bg-red-600/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 text-[11px] font-bold transition flex items-center justify-center gap-1"
            >
              {resolving === "reject" ? "…" : "✕ Reject"}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
function PendingTicketsModal({ counts = { att: 0, leave: 0, other: 0 }, onClose, onResolved, refreshCounts }) {
  const [tab, setTab] = useState("ATTENDANCE");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [noteMap, setNoteMap] = useState({});
  const [leaveTypes, setLeaveTypes] = useState(LEAVE_TYPES);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTickets = useMemo(() => {
    if (!searchQuery.trim()) return tickets;
    const q = searchQuery.toLowerCase().trim();
    return tickets.filter(tk => {
      const name = (tk.employee_name || "").toLowerCase();
      const id = (tk.employee_id || "").toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [tickets, searchQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setEditingId(null);
    setSearchQuery("");
    try {
      const res = await getEditTickets({ status: "PENDING", request_type: tab });
      const all = res.data.tickets || [];
      const filtered = all.filter(tk => tk.request_type === tab);
      setTickets(filtered);
      if (res.data && res.data.leave_types) {
        setLeaveTypes(res.data.leave_types);
      }
      if (refreshCounts) {
        refreshCounts();
      }
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [tab, refreshCounts]);

  useEffect(() => { load(); }, [load]);

  const handleReject = async (id, adminNote) => {
    setResolving(r => ({ ...r, [id]: "reject" }));
    try {
      await resolveTicket(id, "reject", adminNote || "");
      setTickets(t => t.filter(tk => tk.id !== id));
      onResolved && onResolved();
      if (refreshCounts) refreshCounts();
    } catch { /* silent */ }
    finally { setResolving(r => { const n = { ...r }; delete n[id]; return n; }); }
  };

  const handleApproveOther = async (id, adminNote) => {
    setResolving(r => ({ ...r, [id]: "approve" }));
    try {
      await resolveTicket(id, "approve", adminNote || "");
      setTickets(t => t.filter(tk => tk.id !== id));
      onResolved && onResolved();
      if (refreshCounts) refreshCounts();
    } catch { /* silent */ }
    finally { setResolving(r => { const n = { ...r }; delete n[id]; return n; }); }
  };

  const handleSaved = (id) => {
    setTickets(t => t.filter(tk => tk.id !== id));
    setEditingId(null);
    onResolved && onResolved();
  };

  const activeTab = TICKET_TABS.find(t => t.key === tab);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, opacity: 0, y: 28 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.93, opacity: 0, y: 28 }}
        transition={{ type: "spring", bounce: 0.28, duration: 0.45 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-6xl flex flex-col"
        style={{
          background: "linear-gradient(160deg, #0d1527 0%, #0a1020 100%)",
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: 24,
          boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/[0.07] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-base shadow-lg flex-shrink-0">🎟️</div>
            <div>
              <h2 className="text-white font-black text-base leading-tight">Pending Tickets</h2>
              <p className="text-white/35 text-[11px] mt-0.5">Review and resolve employee requests</p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition-all text-sm font-bold">✕</button>
        </div>

        {/* Tab Pills & Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 pt-4 pb-3 flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {TICKET_TABS.map(t => {
              const c = TAB_COLORS[t.color];
              const isActive = tab === t.key;

              const getTabCount = (key) => {
                const c = counts || {};
                if (key === "ATTENDANCE") return c.att || 0;
                if (key === "LEAVE") return c.leave || 0;
                if (key === "PROFILE_UPDATE") return c.other || 0;
                return 0;
              };

              const tabCount = getTabCount(t.key);

              const badgeBg = isActive
                ? t.color === "indigo"
                  ? "bg-indigo-400 text-slate-950"
                  : t.color === "rose"
                    ? "bg-rose-400 text-slate-950"
                    : "bg-amber-400 text-slate-950"
                : t.color === "indigo"
                  ? "bg-indigo-500/15 text-indigo-300 border border-indigo-500/20"
                  : t.color === "rose"
                    ? "bg-rose-500/15 text-rose-300 border border-rose-500/20"
                    : "bg-amber-500/15 text-amber-300 border border-amber-500/20";

              return (
                <button key={t.key} onClick={() => { setTab(t.key); setSearchQuery(""); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all duration-200 ${isActive ? c.active : "bg-white/5 border-white/10 text-white/45 hover:bg-white/10 hover:text-white/70"
                    }`}
                >
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                  {tabCount > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black transition-all ${badgeBg}`}>
                      {tabCount}
                    </span>
                  )}
                  {isActive && tabCount === 0 && <span className={`w-1.5 h-1.5 rounded-full ${c.dot} animate-pulse flex-shrink-0`} />}
                </button>
              );
            })}
          </div>

          {/* Search Bar */}
          <div className="relative w-full sm:w-64 flex-shrink-0">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-white/35 text-xs">🔍</span>
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-white text-xs outline-none focus:border-indigo-400 focus:bg-white/[0.08] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/35 hover:text-white text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Ticket List */}
        <div className="flex-1 overflow-y-auto px-6 pb-6" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,.05) transparent" }}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="w-7 h-7 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" />
              <p className="text-white/30 text-xs font-medium">Loading tickets…</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <span className="text-4xl opacity-30">🎉</span>
              <p className="text-white/35 text-sm font-semibold">No pending {activeTab?.label.toLowerCase()} tickets</p>
              <p className="text-white/20 text-xs">All caught up!</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <span className="text-4xl opacity-30">🔍</span>
              <p className="text-white/35 text-sm font-semibold">No matching users found</p>
              <p className="text-white/20 text-xs">Try searching for a different name or ID</p>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden mt-2 shadow-2xl">
              <div className="overflow-x-auto">
                {tab === "ATTENDANCE" && (
                  <table className="w-full text-xs text-center border-collapse min-w-[750px]">
                    <thead className="bg-white/10 text-white font-semibold">
                      <tr>
                        <th className="py-3 px-3 text-left">Employee</th>
                        <th className="py-3 px-3">Date</th>
                        <th className="py-3 px-3">Status</th>
                        <th className="py-3 px-3">Check In</th>
                        <th className="py-3 px-3">Check Out</th>
                        <th className="py-3 px-3 text-left">Reason</th>
                        <th className="py-3 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.map(tk => (
                        <AttendanceTicketRow
                          key={tk.id}
                          tk={tk}
                          onResolved={() => handleSaved(tk.id)}
                          isEditing={editingId === tk.id}
                          onStartEdit={() => setEditingId(tk.id)}
                          onCancelEdit={() => setEditingId(null)}
                          note={noteMap[tk.id] || ""}
                          setNote={(val) => setNoteMap(prev => ({ ...prev, [tk.id]: val }))}
                          resolving={resolving[tk.id]}
                          onReject={(adminNote) => handleReject(tk.id, adminNote)}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
                {tab === "LEAVE" && (
                  <table className="w-full text-xs text-center border-collapse min-w-[750px]">
                    <thead className="bg-white/10 text-white font-semibold">
                      <tr>
                        <th className="py-3 px-3 text-left">Employee</th>
                        <th className="py-3 px-3">Date</th>
                        <th className="py-3 px-3">Type</th>
                        <th className="py-3 px-3">Status</th>
                        <th className="py-3 px-3 text-left">Reason</th>
                        <th className="py-3 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.map(tk => (
                        <LeaveTicketRow
                          key={tk.id}
                          tk={tk}
                          leaveTypes={leaveTypes}
                          onResolved={() => handleSaved(tk.id)}
                          isEditing={editingId === tk.id}
                          onStartEdit={() => setEditingId(tk.id)}
                          onCancelEdit={() => setEditingId(null)}
                          note={noteMap[tk.id] || ""}
                          setNote={(val) => setNoteMap(prev => ({ ...prev, [tk.id]: val }))}
                          resolving={resolving[tk.id]}
                          onReject={(adminNote) => handleReject(tk.id, adminNote)}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
                {tab === "PROFILE_UPDATE" && (
                  <table className="w-full text-xs text-center border-collapse min-w-[650px]">
                    <thead className="bg-white/10 text-white font-semibold">
                      <tr>
                        <th className="py-3 px-3 text-left">Employee</th>
                        <th className="py-3 px-3">Date</th>
                        <th className="py-3 px-3 text-left">Requested Changes</th>
                        <th className="py-3 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.map(tk => (
                        <ProfileUpdateTicketRow
                          key={tk.id}
                          tk={tk}
                          onResolved={() => handleSaved(tk.id)}
                          note={noteMap[tk.id] || ""}
                          setNote={(val) => setNoteMap(prev => ({ ...prev, [tk.id]: val }))}
                          resolving={resolving[tk.id]}
                          onApprove={(adminNote) => handleApproveOther(tk.id, adminNote)}
                          onReject={(adminNote) => handleReject(tk.id, adminNote)}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PENDING REMOTE REQUESTS POPUP MODAL
// ════════════════════════════════════════════════════════════════════════════
function PendingRemoteRequestsModal({ counts = { remote: 0, wfh: 0 }, onClose, onResolved, refreshCounts }) {
  const [tab, setTab] = useState("PENDING"); // PENDING, APPROVED, ALL
  const [typeFilter, setTypeFilter] = useState("ALL"); // ALL, SINGLE_DAY, WFH_PERIOD
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resolving, setResolving] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setSearchQuery("");
    try {
      const [remoteRes, wfhRes] = await Promise.all([
        getRemoteAttendance({
          status: tab === "ALL" ? undefined : tab,
          include_all: "true"
        }),
        getWfhPeriods({
          status: tab === "ALL" ? undefined : tab
        })
      ]);

      const rems = (remoteRes.data?.remote_requests || []).map(r => ({
        ...r,
        reqType: "SINGLE_DAY",
        displayType: "Single Day Remote",
        dateDisplay: r.attendance_date,
        reasonDisplay: r.remote_reason,
        statusDisplay: r.remote_approval_status,
        timestamp: r.attendance_date
      }));

      const wfhs = (wfhRes.data?.wfh_periods || []).map(p => ({
        ...p,
        reqType: "WFH_PERIOD",
        displayType: "WFH Period",
        dateDisplay: `${p.from_date} to ${p.to_date}`,
        reasonDisplay: p.reason,
        statusDisplay: p.status,
        timestamp: p.from_date
      }));

      const merged = [...rems, ...wfhs].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      setRequests(merged);

      if (refreshCounts) {
        refreshCounts();
      }
    } catch (err) {
      console.error("Failed to load remote requests:", err);
      setError("Failed to load requests. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [tab, refreshCounts]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = async (req, action) => {
    const key = `${req.reqType}-${req.id}`;
    setResolving(r => ({ ...r, [key]: action }));
    setError("");
    try {
      if (req.reqType === "SINGLE_DAY") {
        await resolveRemoteAttendance(req.id, action);
      } else {
        await resolveWfhPeriod(req.id, action);
      }
      setRequests(prev => prev.filter(r => !(r.id === req.id && r.reqType === req.reqType)));
      onResolved && onResolved();
      if (refreshCounts) refreshCounts();
    } catch (err) {
      const d = err?.response?.data;
      setError(d?.error || d?.detail || `Failed to ${action} request.`);
    } finally {
      setResolving(r => {
        const n = { ...r };
        delete n[key];
        return n;
      });
    }
  };

  const filteredRequests = useMemo(() => {
    let list = requests;
    if (typeFilter !== "ALL") {
      list = list.filter(r => r.reqType === typeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(r => {
        const name = (r.employee_name || "").toLowerCase();
        const id = (r.employee_id || "").toLowerCase();
        return name.includes(q) || id.includes(q);
      });
    }
    return list;
  }, [requests, typeFilter, searchQuery]);

  const typeCounts = useMemo(() => {
    let single = 0;
    let period = 0;
    requests.forEach(r => {
      if (r.reqType === "SINGLE_DAY") single++;
      else if (r.reqType === "WFH_PERIOD") period++;
    });
    return { single, period };
  }, [requests]);

  const tabTotal = counts.remote + counts.wfh;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.93, opacity: 0, y: 28 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.93, opacity: 0, y: 28 }}
        transition={{ type: "spring", bounce: 0.28, duration: 0.45 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-6xl flex flex-col"
        style={{
          background: "linear-gradient(160deg, #0d1527 0%, #0a1020 100%)",
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: 24,
          boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/[0.07] flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-base shadow-lg flex-shrink-0">🛰️</div>
            <div>
              <h2 className="text-white font-black text-base leading-tight">Remote & WFH Requests</h2>
              <p className="text-white/35 text-[11px] mt-0.5">Review and manage single-day remote and multi-day WFH requests</p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition-all text-sm font-bold">✕</button>
        </div>

        {/* Tab Selection */}
        <div className="flex items-center justify-between px-6 pt-4 pb-2 border-b border-white/[0.04] flex-shrink-0 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {[
              { key: "PENDING", label: "Pending", icon: "⏳", color: "text-amber-400 bg-amber-500/10 border-amber-500/20 active:bg-amber-500/20", count: tabTotal },
              { key: "APPROVED", label: "Approved", icon: "✅", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20 active:bg-emerald-500/20" },
              { key: "ALL", label: "All Requests", icon: "🗂️", color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20 active:bg-cyan-500/20" }
            ].map(t => {
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setTypeFilter("ALL"); setSearchQuery(""); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all duration-200 ${isActive ? `${t.color} border-current` : "bg-white/5 border-white/10 text-white/45 hover:bg-white/10 hover:text-white/70"
                    }`}
                >
                  <span>{t.icon}</span>
                  <span>{t.label}</span>
                  {t.key === "PENDING" && t.count > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-amber-400 text-slate-950">
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sub-Filters / Type Selector */}
          <div className="flex items-center gap-2">
            {[
              { key: "ALL", label: "All Types", count: requests.length },
              { key: "SINGLE_DAY", label: "Single Day Remote", count: typeCounts.single },
              { key: "WFH_PERIOD", label: "WFH Period", count: typeCounts.period }
            ].map(f => {
              const isActive = typeFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setTypeFilter(f.key)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${isActive
                    ? "bg-white/15 text-white border border-white/20"
                    : "bg-transparent text-white/40 hover:text-white/60 border border-transparent"
                    }`}
                >
                  <span>{f.label}</span>
                  <span className={`ml-1 px-1 rounded bg-white/5 text-[9px] ${isActive ? "text-white" : "text-white/30"}`}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Bar Row */}
        <div className="px-6 py-2.5 border-b border-white/[0.04] flex-shrink-0">
          <div className="relative w-full max-w-md">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-white/35 text-xs">🔍</span>
            <input
              type="text"
              placeholder="Search requests by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-white text-xs outline-none focus:border-orange-400 focus:bg-white/[0.08] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-white/35 hover:text-white text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Errors */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mx-6 mt-4 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs font-semibold flex-shrink-0 flex items-center gap-2">
              ⚠️ {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Requests List */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,.05) transparent" }}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-7 h-7 rounded-full border-2 border-white/10 border-t-white/60 animate-spin" />
              <p className="text-white/30 text-xs font-medium">Loading requests…</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <span className="text-4xl opacity-30">{tab === "PENDING" ? "🎉" : "📭"}</span>
              <p className="text-white/35 text-sm font-semibold">No {tab.toLowerCase()} requests found</p>
              <p className="text-white/20 text-xs">{tab === "PENDING" ? "All remote requests cleared!" : "Nothing to show."}</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <span className="text-4xl opacity-30">🔍</span>
              <p className="text-white/35 text-sm font-semibold">No matching requests found</p>
              <p className="text-white/20 text-xs">Try searching for a different name/ID or changing filters</p>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-center border-collapse min-w-[800px]">
                  <thead className="bg-white/10 text-white font-semibold">
                    <tr>
                      <th className="py-3 px-4 text-left">Employee</th>
                      <th className="py-3 px-3">Type</th>
                      <th className="py-3 px-3">Date(s)</th>
                      <th className="py-3 px-3">Details / Timings</th>
                      <th className="py-3 px-4 text-left">💬 Reason</th>
                      <th className="py-3 px-4">Status / Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map(req => {
                      const isSingle = req.reqType === "SINGLE_DAY";
                      const key = `${req.reqType}-${req.id}`;
                      const isResolving = !!resolving[key];

                      return (
                        <tr key={key} className="border-b border-white/5 hover:bg-white/5 transition duration-150">
                          {/* Employee info */}
                          <td className="py-3 px-4 text-left">
                            <div className="flex items-center gap-3">
                              <Avatar name={req.employee_name} size={32} />
                              <div className="min-w-0">
                                <p className="text-white font-semibold truncate leading-snug">{req.employee_name}</p>
                                <p className="text-white/40 text-[10px] truncate leading-none mt-0.5">ID: {req.employee_id}</p>
                              </div>
                            </div>
                          </td>

                          {/* Request Type */}
                          <td className="py-3 px-3">
                            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-wider uppercase border ${isSingle
                              ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/20"
                              : "bg-violet-500/10 text-violet-300 border-violet-500/20"
                              }`}>
                              {req.displayType}
                            </span>
                          </td>

                          {/* Date(s) */}
                          <td className="py-3 px-3 font-semibold text-white/90">
                            {req.dateDisplay}
                          </td>

                          {/* Details / Timing */}
                          <td className="py-3 px-3 text-white/70">
                            {isSingle ? (
                              <div className="flex flex-col gap-0.5 text-[10px] items-center">
                                <span>In: <span className="text-white font-medium">{fmtTimeFromISO(req.check_in)}</span></span>
                                <span>Out: <span className="text-white font-medium">{req.check_out ? fmtTimeFromISO(req.check_out) : "—"}</span></span>
                              </div>
                            ) : (
                              <span className="text-[10px] text-white/50 italic">Full Period WFH</span>
                            )}
                          </td>

                          {/* Reason */}
                          <td className="py-3 px-4 text-left max-w-[200px]">
                            <div className="text-white/70 text-[11px] leading-relaxed break-words line-clamp-2" title={req.reasonDisplay}>
                              {req.reasonDisplay || <span className="text-white/20 italic">No reason provided</span>}
                            </div>
                          </td>

                          {/* Action Buttons */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            {req.statusDisplay === "PENDING" || req.status === "PENDING" ? (
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  onClick={() => handleAction(req, "approve")}
                                  disabled={isResolving}
                                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-[10px] font-black uppercase tracking-wider transition flex items-center gap-1 shadow-md active:scale-95"
                                >
                                  {resolving[key] === "approve" ? (
                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : "✅ Approve"}
                                </button>
                                <button
                                  onClick={() => handleAction(req, "reject")}
                                  disabled={isResolving}
                                  className="px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-600 disabled:opacity-40 text-white text-[10px] font-black uppercase tracking-wider transition flex items-center gap-1 shadow-md active:scale-95"
                                >
                                  {resolving[key] === "reject" ? (
                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : "✕ Reject"}
                                </button>
                              </div>
                            ) : (
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${req.statusDisplay === "APPROVED" || req.status === "APPROVED"
                                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                                : "bg-red-500/10 text-red-400 border border-red-500/20"
                                }`}>
                                {req.statusDisplay || req.status}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// QUICK ACTIONS CARD
// ════════════════════════════════════════════════════════════════════════════
const TICKET_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v2z" />
    <line x1="13" y1="5" x2="13" y2="19" strokeDasharray="3 3" />
  </svg>
);

const LEAVE_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm-5-7H7v2h7v-2z" />
  </svg>
);

const PROFILE_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
  </svg>
);

const ATTENDANCE_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
  </svg>
);

const USERS_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
  </svg>
);

const USER_PLUS_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const LAPTOP_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
  </svg>
);

const DOCUMENT_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
  </svg>
);


const MANAGE_CL_ICON = (
  <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const QuickActionsCard = memo(function QuickActionsCard({
  profile,
  isAdminRole,
  isSuperAdmin = false,
  setShowLeaveModal,
  setShowProfileModal,
  setShowLiveSupportModal,
  setShowHolidaysModal,
  setShowManageClModal,
  supportNotificationCount = 0,
  pendingTicketCount = 0,
  onPendingTickets,
  pendingRemoteCount = 0,
  onPendingRemoteRequests,
  onRiseTicketAttendance,
  onRiseTicketLeave,
  onRiseTicketOther,
  onOpenPolicies,
  onOpenAddAnnouncement,
  className = ""
}) {
  const navigate = useNavigate();
  const role = profile.role || useAuthStore.getState().role || "EMPLOYEE";
  const [showRiseTicketMenu, setShowRiseTicketMenu] = useState(false);

  // Build special actions array separately so we can add badges
  const specialActions = [];

  // Apply Leave + My Profile (non-admin)
  if (role !== "ADMIN") {
    specialActions.push(
      {
        label: "Apply Leave",
        sub: "Time Off",
        icon: LEAVE_ICON,
        color: "bg-gradient-to-br from-rose-500 to-red-600 hover:from-rose-400 hover:to-red-500 border-rose-400/30 shadow-[0_4px_12px_rgba(244,63,94,0.2)]",
        action: () => setShowLeaveModal(true)
      },
      {
        label: "My Profile",
        sub: "Account",
        icon: PROFILE_ICON,
        color: "bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 border-blue-400/30 shadow-[0_4px_12px_rgba(37,99,235,0.2)]",
        action: () => setShowProfileModal(true)
      }
    );
  }

  // Attendance Report (ADMIN) / My Attendance (others)
  if (role === "ADMIN") {
    specialActions.push({
      label: "Attendance Report",
      sub: "Logs",
      icon: ATTENDANCE_ICON,
      color: "bg-gradient-to-br from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 border-emerald-500/30 shadow-[0_4px_12px_rgba(16,185,129,0.25)]",
      action: () => navigate("/admin/location-attendance-report")
    });
  } else {
    specialActions.push({
      label: "My Attendance",
      sub: "Check In",
      icon: ATTENDANCE_ICON,
      color: "bg-gradient-to-br from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 border-emerald-400/30 shadow-[0_4px_12px_rgba(16,185,129,0.25)]",
      action: () => navigate(`/${role.toLowerCase()}/attendance-report`)
    });
  }

  // View Employee's (ADMIN and MANAGER only)
  if (role === "ADMIN") {
    specialActions.push({
      label: "Employees List",
      sub: "Directory",
      icon: USERS_ICON,
      color: "bg-gradient-to-br from-pink-600 to-fuchsia-700 hover:from-pink-500 hover:to-fuchsia-600 border-pink-500/30 shadow-[0_4px_12px_rgba(236,72,153,0.25)]",
      action: () => navigate("/admin/users")
    });
  } else if (role === "MANAGER") {
    specialActions.push({
      label: "Employees List",
      sub: "Directory",
      icon: USERS_ICON,
      color: "bg-gradient-to-br from-purple-600 to-violet-700 hover:from-purple-500 hover:to-violet-600 border-purple-500/30 shadow-[0_4px_12px_rgba(147,51,234,0.25)]",
      action: () => navigate("/manager/users")
    });
  }

  // Create New Employee (ADMIN + MANAGER only)
  if (role === "ADMIN") {
    specialActions.push({
      label: "Create New User",
      sub: "New Hire",
      icon: USER_PLUS_ICON,
      color: "bg-gradient-to-br from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 border-blue-500/30 shadow-[0_4px_12px_rgba(37,99,235,0.25)]",
      action: () => navigate("/admin/create-user")
    });
  } else if (role === "MANAGER") {
    specialActions.push({
      label: "Create New User",
      sub: "New Hire",
      icon: USER_PLUS_ICON,
      color: "bg-gradient-to-br from-cyan-500 to-sky-600 hover:from-cyan-400 hover:to-sky-500 border-cyan-400/30 shadow-[0_4px_12px_rgba(6,182,212,0.25)]",
      action: () => navigate("/manager/create-user")
    });
  }

  // Pending Remote Request
  if (role === "ADMIN" || role === "MANAGER") {
    specialActions.push({
      label: "Pending Remote Req",
      sub: "Approve",
      icon: LAPTOP_ICON,
      color: "bg-gradient-to-br from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-500 border-orange-500/30 shadow-[0_4px_12px_rgba(249,115,22,0.25)]",
      action: onPendingRemoteRequests,
      badgeCount: pendingRemoteCount
    });
  }

  // Company Policy's
  specialActions.push({
    label: "Company Policies",
    sub: "Handbook",
    icon: DOCUMENT_ICON,
    color: role === "ADMIN"
      ? "bg-gradient-to-br from-purple-600 to-indigo-700 hover:from-purple-500 hover:to-indigo-600 border-purple-500/30 shadow-[0_4px_12px_rgba(124,58,237,0.25)]"
      : "bg-gradient-to-br from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 border-pink-400/30 shadow-[0_4px_12px_rgba(236,72,153,0.25)]",
    action: onOpenPolicies
  });

  // Holiday List (accessible to all) — HIDDEN: commented out per request
  // specialActions.push({
  //   label: "Holidays List",
  //   sub: "Calendar",
  //   icon: CALENDAR_ICON,
  //   color: role === "ADMIN"
  //     ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 border-violet-400/30 shadow-[0_4px_12px_rgba(139,92,246,0.25)]"
  //     : "bg-gradient-to-br from-violet-500 to-fuchsia-600 hover:from-violet-400 hover:to-fuchsia-500 border-violet-400/30 shadow-[0_4px_12px_rgba(139,92,246,0.2)]",
  //   action: () => setShowHolidaysModal(true)
  // });

  // Add Announcement (ADMIN only)
  if (role === "ADMIN") {
    specialActions.push({
      label: "Add Announcement",
      sub: "Broadcast",
      icon: (
        <svg className="w-5 h-5 opacity-85" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      ),
      color: "bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 border-amber-400/30 shadow-[0_4px_12px_rgba(245,158,11,0.25)]",
      action: onOpenAddAnnouncement
    });
  }

  // Manage CL (Super Admin only)
  if (isSuperAdmin) {
    specialActions.push({
      label: "Manage CL",
      sub: "Balances",
      icon: MANAGE_CL_ICON,
      color: "bg-gradient-to-br from-indigo-600 to-violet-700 hover:from-indigo-500 hover:to-violet-600 border-indigo-500/30 shadow-[0_4px_12px_rgba(79,70,229,0.25)]",
      action: () => setShowManageClModal(true)
    });
  }

  // Live Support Action — shown only to super admins via the floating bubble,
  // NOT as a Quick Action button (removed per design requirement).

  return (
    <motion.div variants={fadeUp} custom={1} initial="hidden" animate="visible" className={`flex flex-col h-full ${showRiseTicketMenu ? "relative z-40" : "relative z-10"} ${className}`}>
      <GlassCard className="p-4 flex flex-col gap-3 h-full">
        <h3 className="text-white font-semibold text-xs leading-none">Quick Actions</h3>
        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${role === "ADMIN" ? "flex-1 h-full min-h-0" : ""}`}>
          {/* Pending Tickets — ADMIN only, with badge */}
          {role === "ADMIN" && (
            <div className={`relative ${role === "ADMIN" ? "h-full" : ""}`}>
              <motion.button
                whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}
                onClick={onPendingTickets}
                className={`w-full flex items-center justify-between rounded-xl transition-all text-left border border-red-500/30 bg-gradient-to-br from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600 text-white shadow-[0_4px_12px_rgba(239,68,68,0.25)]
                  ${role === "ADMIN" ? "h-full min-h-[64px] sm:min-h-[76px] md:min-h-[86px] px-3.5 py-3" : "h-10 px-2.5"}`}
              >
                <div className="flex flex-col min-w-0 pr-1 select-none">
                  <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-white/50 leading-none mb-1">
                    Review
                  </span>
                  <span className="text-[10px] sm:text-[11px] md:text-[12px] font-black uppercase tracking-wide text-white leading-tight break-words">
                    Tickets
                  </span>
                </div>
                <div className="text-white/85 flex-shrink-0 ml-3 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 transition-all duration-200">
                  {React.cloneElement(TICKET_ICON, { className: "w-full h-full opacity-90" })}
                </div>
              </motion.button>
              {pendingTicketCount > 0 && (
                <span className="absolute -top-2.5 -right-2 min-w-[22px] h-[22px] sm:min-w-[24px] sm:h-[24px] px-1 bg-red-500 rounded-full text-white text-[11px] sm:text-xs font-black flex items-center justify-center border-2 border-slate-950 animate-pulse z-10 shadow-lg select-none">
                  {pendingTicketCount > 99 ? "99+" : pendingTicketCount}
                </span>
              )}
            </div>
          )}

          {/* Standard action buttons */}
          {specialActions.map((a, i) => (
            <div key={i} className={`relative ${role === "ADMIN" ? "h-full" : ""}`}>
              <motion.button
                whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}
                onClick={a.action}
                className={`w-full flex items-center justify-between rounded-xl transition-all text-left border text-white ${a.color}
                  ${role === "ADMIN" ? "h-full min-h-[64px] sm:min-h-[76px] md:min-h-[86px] px-3.5 py-3" : "h-10 px-2.5"}`}
              >
                {role === "ADMIN" ? (
                  <>
                    <div className="flex flex-col min-w-0 pr-1 select-none">
                      <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-white/50 leading-none mb-1">
                        {a.sub}
                      </span>
                      <span className="text-[10px] sm:text-[11px] md:text-[12px] font-black uppercase tracking-wide text-white leading-tight break-words">
                        {a.label}
                      </span>
                    </div>
                    <div className="text-white/85 flex-shrink-0 ml-3 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 transition-all duration-200">
                      {React.cloneElement(a.icon, { className: "w-full h-full opacity-90" })}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-white leading-tight truncate pr-1 select-none">
                      {a.label}
                    </span>
                    <div className="text-white/70 flex-shrink-0">
                      <div className="scale-90 flex items-center justify-center">
                        {a.icon}
                      </div>
                    </div>
                  </>
                )}
              </motion.button>
              {a.badgeCount > 0 && (
                <span className="absolute -top-2.5 -right-2 min-w-[22px] h-[22px] sm:min-w-[24px] sm:h-[24px] px-1 bg-amber-500 rounded-full text-[#091122] text-[11px] sm:text-xs font-black flex items-center justify-center border-2 border-slate-950 animate-pulse z-10 shadow-lg select-none">
                  {a.badgeCount > 99 ? "99+" : a.badgeCount}
                </span>
              )}
            </div>
          ))}

          {/* Rise Ticket Button & Dropdown — Manager & Employee only */}
          {(role === "MANAGER" || role === "EMPLOYEE") && (
            <div className="relative">
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => setShowRiseTicketMenu((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-xl transition-all h-10 text-left px-2.5 bg-gradient-to-br from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white border border-fuchsia-400/30 shadow-[0_4px_12px_rgba(217,70,239,0.2)]"
              >
                <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-white leading-tight truncate pr-1 select-none">
                  Rise Ticket ▾
                </span>
                <div className="text-white/70 flex-shrink-0">
                  <div className="scale-90 flex items-center justify-center">
                    {TICKET_ICON}
                  </div>
                </div>
              </motion.button>
              <AnimatePresence>
                {showRiseTicketMenu && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowRiseTicketMenu(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-2 w-48 bg-slate-900 border border-white/20 rounded-xl shadow-2xl p-1 z-30 flex flex-col"
                    >
                      <button
                        onClick={() => {
                          setShowRiseTicketMenu(false);
                          onRiseTicketAttendance();
                        }}
                        className="w-full text-left px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg text-xs font-semibold transition"
                      >
                        🎫 Ticket for Attendance
                      </button>
                      <button
                        onClick={() => {
                          setShowRiseTicketMenu(false);
                          onRiseTicketLeave();
                        }}
                        className="w-full text-left px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg text-xs font-semibold transition"
                      >
                        🎫 Ticket for Leave
                      </button>
                      <button
                        onClick={() => {
                          setShowRiseTicketMenu(false);
                          onRiseTicketOther();
                        }}
                        className="w-full text-left px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg text-xs font-semibold transition"
                      >
                        🎫 Ticket for Other Issues
                      </button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </GlassCard>
    </motion.div>
  );
});

// ════════════════════════════════════════════════════════════════════════════
// RECENT ANNOUNCEMENTS CARD
// ════════════════════════════════════════════════════════════════════════════

const RecentAnnouncementsCard = memo(function RecentAnnouncementsCard({ className = "", onViewAll, announcements = [], onSelectAnnouncement }) {
  return (
    <motion.div variants={fadeUp} custom={5} initial="hidden" animate="visible" className={`flex flex-col ${className}`}>
      <GlassCard className="p-4 sm:p-5 flex flex-col h-full">
        <style>{`
          @keyframes ribbon-glow-pulse {
            0%, 100% {
              transform: scale(0.95);
              filter: drop-shadow(0 2px 4px rgba(249, 115, 22, 0.35)) drop-shadow(0 0 4px rgba(124, 58, 237, 0.25));
            }
            50% {
              transform: scale(1.08);
              filter: drop-shadow(0 6px 16px rgba(249, 115, 22, 0.75)) drop-shadow(0 0 16px rgba(124, 58, 237, 0.65));
            }
          }
          .animate-ribbon-glow {
            animation: ribbon-glow-pulse 1.4s infinite ease-in-out;
          }
        `}</style>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-sm">Recent Announcements</span>
          </div>
          <button onClick={onViewAll} className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors">View All</button>
        </div>
        <div className="flex-1 divide-y divide-white/[0.06] overflow-y-auto" style={{ maxHeight: 260, scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,.05) transparent" }}>
          {announcements.slice(0, 2).map((a) => (
            <div
              key={a.id}
              onClick={() => onSelectAnnouncement && onSelectAnnouncement(a)}
              className="flex items-center gap-4 py-3.5 hover:text-blue-400 transition-all cursor-pointer group select-none"
            >
              {/* 3D Folded Ribbon "NEW UPDATE" Badge */}
              <div className="flex-shrink-0 animate-ribbon-glow">
                <svg className="w-16 h-12" viewBox="0 0 100 72">
                  <defs>
                    <linearGradient id="newGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#F97316" />
                      <stop offset="100%" stopColor="#A855F7" />
                    </linearGradient>
                    <linearGradient id="updateGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#7C3AED" />
                      <stop offset="100%" stopColor="#5B21B6" />
                    </linearGradient>
                  </defs>

                  {/* Right bottom fold shadow */}
                  <polygon points="68,57 70,67 86,57" fill="#3B0764" />

                  {/* Main purple bottom ribbon */}
                  <polygon points="10,26 95,26 88,57 3,57" fill="url(#updateGrad)" />

                  {/* Left fold shadow */}
                  <polygon points="3,26 11,26 11,35" fill="#4C1D95" />

                  {/* Top orange-purple ribbon */}
                  <polygon points="8,5 60,5 55,26 3,26" fill="url(#newGrad)" />

                  {/* Texts */}
                  <text
                    x="31.5"
                    y="20"
                    textAnchor="middle"
                    fill="#FFFFFF"
                    fontSize="14"
                    fontWeight="900"
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    NEW
                  </text>
                  <text
                    x="49"
                    y="47"
                    textAnchor="middle"
                    fill="#FFFFFF"
                    fontSize="17"
                    fontWeight="900"
                    fontFamily="Inter, system-ui, sans-serif"
                    letterSpacing="0.5"
                  >
                    UPDATE
                  </text>
                </svg>
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-white/90 group-hover:text-blue-400 text-sm sm:text-base font-bold leading-tight truncate transition-colors">
                  {a.title}
                </p>
              </div>
            </div>
          ))}
          {announcements.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-white/25 text-xs border-0">
              <span>🔔</span>
              <p className="mt-1">No announcements yet</p>
            </div>
          )}
        </div>
      </GlassCard>
    </motion.div>
  );
});

// ════════════════════════════════════════════════════════════════════════════
// LIVE CHAT SIDEBAR WITH OS-STYLE WINDOW CONTROLS
// ════════════════════════════════════════════════════════════════════════════
function LiveChatSidebar({ chatState, setChatState, isSuperAdmin }) {
  const isMinimized = chatState === "minimized";

  return (
    <div className={`flex flex-col rounded-2xl overflow-hidden transition-all duration-300 ${chatState === "maximized"
      ? "w-full h-full shadow-[0_0_50px_rgba(0,0,0,0.8)]"
      : isMinimized
        ? "h-12 w-[300px]"
        : "h-[500px] xl:h-[600px] w-full"
      }`}
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
        backdropFilter: "blur(20px)"
      }}
    >
      {/* Chat header */}
      <div className="flex items-center justify-between px-4 py-3 select-none flex-shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "rgba(0,0,0,0.15)" }}>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-white font-semibold text-sm">Live Support Chat</span>
        </div>

        {/* OS style controls: minus, square/restore, cross */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setChatState(isMinimized ? "open" : "minimized")}
            className="w-5 h-5 rounded bg-white/5 border border-white/10 hover:bg-white/15 active:scale-95 flex items-center justify-center text-white/50 hover:text-white transition-all text-[10px] font-bold"
            title="Minimize"
          >
            —
          </button>
          <button
            onClick={() => setChatState(chatState === "maximized" ? "open" : "maximized")}
            className="w-5 h-5 rounded bg-white/5 border border-white/10 hover:bg-white/15 active:scale-95 flex items-center justify-center text-white/50 hover:text-white transition-all text-[9px] font-bold"
            title={chatState === "maximized" ? "Restore" : "Maximize"}
          >
            {chatState === "maximized" ? "❐" : "⬜"}
          </button>
          <button
            onClick={() => setChatState("closed")}
            className="w-5 h-5 rounded bg-red-500/10 border border-red-500/20 hover:bg-red-500/25 active:scale-95 flex items-center justify-center text-red-400 hover:text-red-200 transition-all text-[10px] font-bold"
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="flex-1 min-h-0 relative">
          <React.Suspense fallback={
            <div className="flex items-center justify-center h-full text-white/30 text-xs">
              Loading support...
            </div>
          }>
            <LiveSupport isPopup={true} onClose={() => setChatState("closed")} isSuperAdmin={isSuperAdmin} />
          </React.Suspense>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN PROFILE PAGE / DASHBOARD Restructured
// ════════════════════════════════════════════════════════════════════════════
function ProfilePage({ Layout, roleTitle, editable = false, defaultTab = "attendance" }) {
  const navigate = useNavigate();
  const isEditing = false;
  const [profile, setProfile] = useState({
    employee_id: useAuthStore.getState().employeeId || "",
    full_name: useAuthStore.getState().fullName || "",
    email: localStorage.getItem("email") || "",
    date_of_birth: "",
    mobile_number: "",
    designation: "",
    department: "",
    role: useAuthStore.getState().role || "",
    status: "Inactive",
    location: "",
    manager_display: "",
  });

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const profileLoaded = profile.role !== "";
  const isAdminRole = profile.role === "ADMIN";

  // Modal display states
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [showRemoteRequestsModal, setShowRemoteRequestsModal] = useState(false);
  const [showLiveSupportModal, setShowLiveSupportModal] = useState(false);
  const [showHolidaysModal, setShowHolidaysModal] = useState(false);
  const [showManageClModal, setShowManageClModal] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("support") === "open") {
      setShowLiveSupportModal(true);
      
      // Silently clean up the search query from address bar
      const cleanUrl = new URL(window.location);
      cleanUrl.searchParams.delete("support");
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const [activeViewAllModal, setActiveViewAllModal] = useState(null); // "leave" | "birthdays" | "announcements" | null

  // Announcements dynamic state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const fetchAnnouncements = useCallback(async (isSilent = false) => {
    if (!isSilent) setAnnouncementsLoading(true);
    try {
      const res = await getAnnouncements();
      setAnnouncements(res.data?.announcements || []);
    } catch (err) {
      console.error("Failed to load announcements", err);
    } finally {
      if (!isSilent) setAnnouncementsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements(false);
    const intervalId = setInterval(() => fetchAnnouncements(true), 30000);
    return () => clearInterval(intervalId);
  }, [fetchAnnouncements]);

  // Rise Ticket Popup modal display states
  const [showAttendancePopup, setShowAttendancePopup] = useState(false);
  const [showLeavePopup, setShowLeavePopup] = useState(false);
  const [showOtherTicketModal, setShowOtherTicketModal] = useState(false);
  const [otherTicketReason, setOtherTicketReason] = useState("");
  const [otherTicketSubmitting, setOtherTicketSubmitting] = useState(false);
  const [otherTicketError, setOtherTicketError] = useState("");
  const [otherTicketSuccess, setOtherTicketSuccess] = useState("");

  const handleRaiseOtherTicket = async () => {
    const sanitizedReason = sanitizeText(otherTicketReason);
    if (!sanitizedReason) {
      setOtherTicketError("Please provide a valid, safe description of the issue.");
      return;
    }
    setOtherTicketSubmitting(true);
    setOtherTicketError("");
    setOtherTicketSuccess("");
    try {
      await raiseEditTicket({
        request_type: "PROFILE_UPDATE",
        reason: sanitizedReason
      });
      setOtherTicketSuccess("Ticket raised successfully!");
      setOtherTicketReason("");
      setTimeout(() => {
        setShowOtherTicketModal(false);
        setOtherTicketSuccess("");
      }, 1500);
    } catch (err) {
      setOtherTicketError(err.response?.data?.error || "Failed to raise ticket.");
    } finally {
      setOtherTicketSubmitting(false);
    }
  };

  // Live Chat state: "open" | "minimized" | "maximized" | "closed"
  const [chatState, setChatState] = useState("closed");
  const [supportNotificationCount, setSupportNotificationCount] = useState(0);

  const refreshSupportNotificationCount = useCallback(async () => {
    const isSuper = useAuthStore.getState().isSuperuser || isSuperAdmin;
    try {
      const res = await getSupportStats();
      if (res.data) {
        if (isSuper) {
          const activeCount = (res.data.open || 0) + (res.data.in_progress || 0);
          setSupportNotificationCount(activeCount);
        } else {
          setSupportNotificationCount(res.data.unread || 0);
        }
      }
    } catch (err) {
      console.error("Error fetching support notification count:", err);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    refreshSupportNotificationCount();
  }, [refreshSupportNotificationCount]);

  useEffect(() => {
    const handleSupportEvent = () => {
      refreshSupportNotificationCount();
    };
    window.addEventListener("support.conversation.updated", handleSupportEvent);
    window.addEventListener("support.message.new", handleSupportEvent);
    window.addEventListener("support.messages.read", handleSupportEvent);
    return () => {
      window.removeEventListener("support.conversation.updated", handleSupportEvent);
      window.removeEventListener("support.message.new", handleSupportEvent);
      window.removeEventListener("support.messages.read", handleSupportEvent);
    };
  }, [refreshSupportNotificationCount]);

  // Pending ticket count (admin only)
  const { counts: pendingTicketCounts, total: pendingTicketCount, refresh: refreshTicketCount } = usePendingTicketCount(isAdminRole);

  // Pending remote request count (admin & manager only)
  const isRemoteEligible = isAdminRole || profile.role === "MANAGER";
  const { counts: pendingRemoteCounts, total: pendingRemoteCount, refresh: refreshRemoteCount } = usePendingRemoteCount(profileLoaded && isRemoteEligible);

  // Workforce overview and Birthday popup hooks
  const { onLeave, birthdays, loading: dashLoading, error: dashError, refresh } = useDashboard(profileLoaded);
  const { show: showBdayPopup, close: closeBdayPopup, partyOn, activateParty } = useBirthdayPopup(
    profileLoaded ? profile : null
  );

  const mustChangePassword = useAuthStore(state => state.mustChangePassword);
  useEffect(() => {
    if (mustChangePassword) {
      navigate("/reset-password", { replace: true });
    }
  }, [navigate, mustChangePassword]);

  useEffect(() => {
    (async () => {
      try {
        const id = useAuthStore.getState().employeeId;
        if (!id) return;
        const res = await apiClient.get(`/accounts/api/users/${id}/`);
        const u = res.data.user;

        // Update auth store with fetched role, name, photo dynamically
        useAuthStore.getState().setAuth({
          role: u.role,
          fullName: u.full_name || "",
          isSuperuser: u.is_superuser === true
        });
        if (u.photo) {
          const photoPath = u.photo.startsWith("http") ? u.photo
            : u.photo.startsWith("/") ? u.photo
              : `/media/${u.photo}`;
          useAuthStore.getState().setProfilePhoto(photoPath);
        }

        setProfile({
          employee_id: u.employee_id,
          full_name: u.full_name || "",
          email: u.email || "",
          designation: u.designation || "",
          department: u.department || "",
          role: u.role,
          date_of_birth: u.date_of_birth || "",
          mobile_number: u.mobile_number || "",
          status: u.is_active ? "Active" : "Inactive",
          manager: u.manager_id || null,
          manager_display: u.manager_display || "",
          location: u.location_name || "",
          gender: u.gender || "MALE",
        });
        setIsSuperAdmin(u.is_superuser === true);
      } catch (err) {
        console.log("Profile fetch failed:", err);
      }
    })();
  }, []);

  const handleChange = useCallback((f, v) => setProfile(p => ({ ...p, [f]: v })), []);

  const handleEdit = useCallback(
    () => navigate(`/admin/update-user/${profile.employee_id}`),
    [navigate, profile.employee_id]
  );

  return (
    <Layout>
      <PartyBackground active={partyOn} />
      <PartyDecorations active={partyOn} />

      <AnimatePresence>
        {showBdayPopup && (
          <BirthdayPopup name={profile.full_name} gender={profile.gender || "MALE"} onClose={closeBdayPopup} onClosed={activateParty} />
        )}
      </AnimatePresence>

      {/* Party banner strip */}
      <AnimatePresence>
        {partyOn && !showBdayPopup && (
          <motion.div
            initial={{ y: -48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -48, opacity: 0 }}
            transition={{ type: "spring", bounce: 0.35, duration: 0.6 }}
            className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 sm:gap-3
              py-2 sm:py-2.5 px-4 text-center pointer-events-none"
            style={{
              background: "linear-gradient(90deg, rgba(251,191,36,0.15) 0%, rgba(139,92,246,0.15) 50%, rgba(236,72,153,0.15) 100%)",
              borderBottom: "1px solid rgba(251,191,36,0.2)",
              backdropFilter: "blur(12px)",
            }}
          >
            <motion.span animate={{ rotate: [-10, 10, -10] }} transition={{ repeat: Infinity, duration: 1.2 }} className="text-base sm:text-lg flex-shrink-0">🎂</motion.span>
            <span className="text-amber-300 font-bold text-xs sm:text-sm truncate">
              Happy Birthday, {profile.full_name.split(" ")[0]}! Wishing you an amazing day! 🎉
            </span>
            <motion.span animate={{ rotate: [10, -10, 10] }} transition={{ repeat: Infinity, duration: 1.2 }} className="text-base sm:text-lg flex-shrink-0">🎈</motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Dashboard Layout (Full Width, No Reserved Space) */}
      <div className={`min-h-screen py-5 sm:py-7 relative z-10 ${partyOn && !showBdayPopup ? "pt-14 sm:pt-16" : ""}`}>
        <div className="max-w-[1300px] mx-auto px-3 sm:px-4">

          <div className="space-y-6">

            {/* Row 1: Mark Attendance & Quick Actions + Nested Overview side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {isAdminRole ? (
                <div className="flex flex-col h-full">
                  <QuickActionsCard
                    profile={profile}
                    isAdminRole={isAdminRole}
                    isSuperAdmin={isSuperAdmin}
                    setShowLeaveModal={setShowLeaveModal}
                    setShowProfileModal={setShowProfileModal}
                    setShowLiveSupportModal={setShowLiveSupportModal}
                    setShowHolidaysModal={setShowHolidaysModal}
                    setShowManageClModal={setShowManageClModal}
                    supportNotificationCount={supportNotificationCount}
                    pendingTicketCount={pendingTicketCount}
                    onPendingTickets={() => setShowTicketsModal(true)}
                    pendingRemoteCount={pendingRemoteCount}
                    onPendingRemoteRequests={() => setShowRemoteRequestsModal(true)}
                    onRiseTicketAttendance={() => setShowAttendancePopup(true)}
                    onRiseTicketLeave={() => setShowLeavePopup(true)}
                    onRiseTicketOther={() => setShowOtherTicketModal(true)}
                    onOpenPolicies={() => setShowPoliciesModal(true)}
                    onOpenAddAnnouncement={() => setActiveViewAllModal("announcements")}
                    className="flex-1"
                  />
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  <MarkAttendance mode="self" />
                </div>
              )}
              {isAdminRole ? (
                <div className="flex flex-col gap-4 h-full">
                  <RecentAnnouncementsCard className="flex-1" onViewAll={() => setActiveViewAllModal("announcements")} announcements={announcements} onSelectAnnouncement={setSelectedAnnouncement} />
                  <div className="grid grid-cols-2 gap-4">
                    <OnLeaveTodayCard onLeave={onLeave} loading={dashLoading} onViewAll={() => setActiveViewAllModal("leave")} />
                    <TodayBirthdaysCard birthdays={birthdays} loading={dashLoading} onViewAll={() => setActiveViewAllModal("birthdays")} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-4 h-full">
                  <QuickActionsCard
                    profile={profile}
                    isAdminRole={isAdminRole}
                    isSuperAdmin={isSuperAdmin}
                    setShowLeaveModal={setShowLeaveModal}
                    setShowProfileModal={setShowProfileModal}
                    setShowLiveSupportModal={setShowLiveSupportModal}
                    setShowHolidaysModal={setShowHolidaysModal}
                    setShowManageClModal={setShowManageClModal}
                    supportNotificationCount={supportNotificationCount}
                    pendingRemoteCount={pendingRemoteCount}
                    onPendingRemoteRequests={() => setShowRemoteRequestsModal(true)}
                    onRiseTicketAttendance={() => setShowAttendancePopup(true)}
                    onRiseTicketLeave={() => setShowLeavePopup(true)}
                    onRiseTicketOther={() => setShowOtherTicketModal(true)}
                    onOpenPolicies={() => setShowPoliciesModal(true)}
                    onOpenAddAnnouncement={() => setActiveViewAllModal("announcements")}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <OnLeaveTodayCard onLeave={onLeave} loading={dashLoading} onViewAll={() => setActiveViewAllModal("leave")} />
                    <TodayBirthdaysCard birthdays={birthdays} loading={dashLoading} onViewAll={() => setActiveViewAllModal("birthdays")} />
                  </div>
                  <RecentAnnouncementsCard className="flex-1" onViewAll={() => setActiveViewAllModal("announcements")} announcements={announcements} onSelectAnnouncement={setSelectedAnnouncement} />
                </div>
              )}
            </div>

            {/* Error messages if overview loading failed */}
            {dashError && (
              <div className="px-4 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/20
                text-rose-300 text-xs font-medium flex items-center gap-2 flex-wrap">
                <span>⚠</span>{dashError}
                <button onClick={refresh} className="ml-auto underline opacity-60 hover:opacity-100">Retry</button>
              </div>
            )}

            {/* Row 2: Leave Calendar full width (100%) */}
            {profileLoaded && (
              <div className="pt-2">
                <LeaveCalendar />
              </div>
            )}

          </div>

        </div>
      </div>

      {/* Pending Tickets Modal (Admin only) */}
      <AnimatePresence>
        {showTicketsModal && (
          <PendingTicketsModal
            counts={pendingTicketCounts}
            onClose={() => setShowTicketsModal(false)}
            onResolved={() => { refreshTicketCount(); }}
            refreshCounts={refreshTicketCount}
          />
        )}
      </AnimatePresence>

      {/* Pending Remote Requests Modal (Admin & Manager only) */}
      <AnimatePresence>
        {showRemoteRequestsModal && (isAdminRole || profile.role === "MANAGER") && (
          <PendingRemoteRequestsModal
            counts={pendingRemoteCounts}
            onClose={() => setShowRemoteRequestsModal(false)}
            onResolved={() => { refreshRemoteCount(); }}
            refreshCounts={refreshRemoteCount}
          />
        )}
      </AnimatePresence>

      {/* Live Support Modal */}
      <AnimatePresence>
        {showLiveSupportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4"
          >
            <div className="w-full max-w-7xl h-[85vh] bg-slate-900 border border-white/20 rounded-2xl shadow-2xl flex flex-col overflow-hidden relative">
              <button
                onClick={() => setShowLiveSupportModal(false)}
                className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
              >
                ✕
              </button>
              <div className="flex-1 overflow-hidden p-3 sm:p-6">
                <React.Suspense fallback={<div className="flex items-center justify-center h-full text-white/50">Loading support...</div>}>
                  <LiveSupport isPopup={true} onClose={() => setShowLiveSupportModal(false)} isSuperAdmin={isSuperAdmin} />
                </React.Suspense>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attendance Report Popup Modal */}
      <AnimatePresence>
        {showAttendancePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md"
            onClick={() => setShowAttendancePopup(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-7xl w-[95vw] bg-slate-950/95 border border-white/10 rounded-3xl shadow-2xl relative p-5 sm:p-6 max-h-[90vh] flex flex-col overflow-hidden no-scrollbar"
            >
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
                  📋 Attendance Report (Raise Ticket Mode)
                </h3>
                <button
                  onClick={() => setShowAttendancePopup(false)}
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 no-scrollbar" style={{ scrollbarWidth: 'none' }}>
                <React.Suspense fallback={<div className="text-white/60 p-8 text-center flex items-center justify-center gap-2"><span className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin inline-block" />Loading Attendance Report...</div>}>
                  <AttendanceReport isPopup={true} onClose={() => setShowAttendancePopup(false)} />
                </React.Suspense>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leave Report Popup Modal */}
      <AnimatePresence>
        {showLeavePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md"
            onClick={() => setShowLeavePopup(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-[98vw] max-w-[98vw] bg-slate-950/95 border border-white/10 rounded-3xl shadow-2xl relative p-5 sm:p-6 max-h-[90vh] flex flex-col overflow-hidden no-scrollbar"
            >
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-sm sm:text-base font-bold text-white flex items-center gap-2">
                  📋 Leave Report (Raise Ticket Mode)
                </h3>
                <button
                  onClick={() => setShowLeavePopup(false)}
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 no-scrollbar" style={{ scrollbarWidth: 'none' }}>
                <React.Suspense fallback={<div className="text-white/60 p-8 text-center flex items-center justify-center gap-2"><span className="w-5 h-5 border-2 border-pink-400 border-t-transparent rounded-full animate-spin inline-block" />Loading Leave Report...</div>}>
                  <LeaveReport isPopup={true} onClose={() => setShowLeavePopup(false)} />
                </React.Suspense>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Holidays List Modal */}
      <AnimatePresence>
        {showHolidaysModal && (
          <HolidaysModal
            role={profile.role || useAuthStore.getState().role || "EMPLOYEE"}
            onClose={() => setShowHolidaysModal(false)}
          />
        )}
      </AnimatePresence>
      {/* Manage CL Modal */}
      <AnimatePresence>
        {showManageClModal && (
          <ManageCLModal
            onClose={() => setShowManageClModal(false)}
          />
        )}
      </AnimatePresence>
      {/* General / Other Issues Ticket Modal */}
      <AnimatePresence>
        {showOtherTicketModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
            onClick={() => {
              setShowOtherTicketModal(false);
              setOtherTicketReason("");
              setOtherTicketError("");
              setOtherTicketSuccess("");
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-slate-900 border border-white/20 rounded-2xl shadow-2xl p-5 sm:p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  🎫 Raise Other Issue Ticket
                </h3>
                <button
                  onClick={() => {
                    setShowOtherTicketModal(false);
                    setOtherTicketReason("");
                    setOtherTicketError("");
                    setOtherTicketSuccess("");
                  }}
                  className="text-white/40 hover:text-white text-xl transition"
                >
                  ✕
                </button>
              </div>

              <p className="text-white/50 text-xs sm:text-sm mb-4">
                Describe the profile correction, general issue, or other requests. The admin will review and apply the fix.
              </p>

              <textarea
                rows={4}
                value={otherTicketReason}
                onChange={(e) => setOtherTicketReason(e.target.value)}
                placeholder="e.g. Correct designation from Trainee to Associate Developer, or correct mobile number."
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white
                                          text-sm placeholder-white/30 resize-none focus:outline-none focus:border-purple-400"
              />

              {otherTicketError && <p className="text-red-400 text-xs mt-2">{otherTicketError}</p>}
              {otherTicketSuccess && <p className="text-emerald-400 text-xs mt-2">{otherTicketSuccess}</p>}

              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowOtherTicketModal(false);
                    setOtherTicketReason("");
                    setOtherTicketError("");
                    setOtherTicketSuccess("");
                  }}
                  className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRaiseOtherTicket}
                  disabled={otherTicketSubmitting}
                  className="flex-1 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50
                                            text-white text-sm font-bold transition"
                >
                  {otherTicketSubmitting ? "Submitting…" : "Submit Ticket"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Modal Overlay for Apply Leave */}
      <AnimatePresence>
        {showLeaveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto"
            onClick={() => setShowLeaveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl bg-slate-950/90 border border-white/10 rounded-3xl shadow-2xl relative p-1 max-h-[90vh] overflow-y-auto"
            >
              <React.Suspense fallback={<div className="text-white/60 p-8 text-center flex items-center justify-center gap-2"><span className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin inline-block" />Loading Apply Leave Form...</div>}>
                <RequestLeave mode="self" onClose={() => setShowLeaveModal(false)} />
              </React.Suspense>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Modal Overlay for My Profile */}
      <AnimatePresence>
        {showProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl bg-[#091122]/95 border border-white/10 rounded-3xl shadow-2xl relative p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
            >
              <button
                onClick={() => setShowProfileModal(false)}
                className="absolute top-6 right-6 z-50 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
              >
                ✕
              </button>
              <ProfileHero
                profile={profile}
                editable={editable}
                activeTab="profile"
                partyOn={partyOn}
                onEdit={handleEdit}
                isSuperAdmin={isSuperAdmin}
              />
              <div className="mt-6">
                <ProfileForm
                  profile={profile}
                  editable={editable}
                  isEditing={isEditing}
                  onChange={handleChange}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-Screen Maximized Live Chat Overlay */}
      <AnimatePresence>
        {chatState === "maximized" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/80 backdrop-blur-md"
            onClick={() => setChatState("open")}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl h-[80vh] shadow-2xl relative"
            >
              <LiveChatSidebar chatState={chatState} setChatState={setChatState} isSuperAdmin={isSuperAdmin} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating LinkedIn-Style Support Chat overlay */}
      <AnimatePresence>
        {chatState !== "closed" && chatState !== "maximized" && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: "spring", bounce: 0.25, duration: 0.4 }}
            className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)]"
          >
            <LiveChatSidebar chatState={chatState} setChatState={setChatState} isSuperAdmin={isSuperAdmin} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Reopen Chat Trigger Bubble when closed */}
      {chatState === "closed" && (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setChatState("open")}
              className="flex items-center gap-2 px-5 py-3 rounded-full text-white font-bold shadow-2xl transition-all border border-blue-400/20"
              style={{ background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)" }}
            >
              <span className="text-lg">💬</span>
              <span className="text-xs tracking-wide">Live Support</span>
            </motion.button>
            {Number(supportNotificationCount) > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center border-2 border-slate-950 animate-pulse shadow-lg select-none">
                {Number(supportNotificationCount)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* View All Leave Today Modal */}
      <AnimatePresence>
        {activeViewAllModal === "leave" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto"
            onClick={() => setActiveViewAllModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl relative p-6 max-h-[80vh] overflow-y-auto"
            >
              <button
                onClick={() => setActiveViewAllModal(null)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
              >
                ✕
              </button>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-xl">🚫</span> On Leave Today
              </h3>
              <div className="space-y-3">
                {onLeave.length === 0 ? (
                  <p className="text-white/40 text-sm text-center py-6">No employees on leave today.</p>
                ) : (
                  onLeave.map((emp) => (
                    <div key={emp.employee_id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                      <Avatar name={emp.full_name} photo={normalizePhoto(emp.photo)} size={36} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{emp.full_name}</p>
                        <p className="text-white/40 text-xs truncate">ID: {emp.employee_id}</p>
                      </div>
                      <span className="text-[10px] font-bold px-2 py-1 rounded bg-rose-500/10 border border-rose-500/20 text-rose-300">
                        {emp.leave_type || "On Leave"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View All Today's Birthdays Modal */}
      <AnimatePresence>
        {activeViewAllModal === "birthdays" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto"
            onClick={() => setActiveViewAllModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl relative p-6 max-h-[80vh] overflow-y-auto"
            >
              <button
                onClick={() => setActiveViewAllModal(null)}
                className="absolute top-5 right-5 w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
              >
                ✕
              </button>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-xl">🎂</span> Celebrating Birthdays
              </h3>
              <div className="space-y-3">
                {birthdays.length === 0 ? (
                  <p className="text-white/40 text-sm text-center py-6">No upcoming birthdays.</p>
                ) : (
                  birthdays.map((emp) => (
                    <div key={emp.employee_id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                      <Avatar name={emp.full_name} photo={normalizePhoto(emp.photo)} size={36} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-semibold truncate">{emp.full_name}</p>
                        <p className="text-white/40 text-xs truncate">ID: {emp.employee_id}</p>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-1 rounded border ${emp.days_away === 0
                        ? "bg-amber-500/10 border-amber-500/20 text-amber-300"
                        : "bg-purple-500/10 border-purple-500/20 text-purple-300"
                        }`}>
                        {emp.label}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View All Announcements Modal */}
      <AnimatePresence>
        {/* All Announcements Modal */}
        <AnimatePresence>
          {activeViewAllModal === "announcements" && (
            <AnnouncementsModal
              role={profile.role}
              announcements={announcements}
              loading={announcementsLoading}
              onRefresh={fetchAnnouncements}
              onClose={() => setActiveViewAllModal(null)}
            />
          )}
        </AnimatePresence>
      </AnimatePresence>

      {/* Company Policies Modal */}
      <AnimatePresence>
        {showPoliciesModal && (
          <CompanyPoliciesModal
            role={profile.role}
            onClose={() => setShowPoliciesModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Announcement Detail Pop-up Modal */}
      <AnimatePresence>
        {selectedAnnouncement && (
          <AnnouncementDetailModal
            announcement={selectedAnnouncement}
            onClose={() => setSelectedAnnouncement(null)}
          />
        )}
      </AnimatePresence>

    </Layout>
  );
}

// ============================================================================
// COMPANY POLICIES MODAL
// ============================================================================
function CompanyPoliciesModal({ role, onClose }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Edit State
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editError, setEditError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  // Delete/Confirmation State
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Preview Overlay State
  const [previewFile, setPreviewFile] = useState(null); // { title, url, name }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState("");
  const [previewError, setPreviewError] = useState("");

  const handleClosePreview = () => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
    }
    setPreviewFile(null);
    setPreviewBlobUrl("");
    setPreviewError("");
    setPreviewLoading(false);
  };

  useEffect(() => {
    if (!previewFile) {
      return;
    }

    const ext = getFileExtension(previewFile.name);
    if (ext === "pdf" || ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext)) {
      setPreviewLoading(true);
      setPreviewError("");

      apiClient.get(previewFile.url, { responseType: 'blob' })
        .then(res => {
          const contentType = res.headers['content-type'] || (ext === 'pdf' ? 'application/pdf' : 'image/' + ext);
          const blob = new Blob([res.data], { type: contentType });
          const blobUrl = URL.createObjectURL(blob);
          setPreviewBlobUrl(blobUrl);
        })
        .catch(err => {
          console.error("Failed to load preview blob", err);
          setPreviewError("Could not load preview. Please download the file to view it.");
        })
        .finally(() => {
          setPreviewLoading(false);
        });
    }
  }, [previewFile]);

  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const isAdmin = role === "ADMIN";

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCompanyPolicies();
      setPolicies(res.data?.policies || []);
    } catch (err) {
      console.error("Failed to load policies", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
    const intervalId = setInterval(fetchPolicies, 30000);
    return () => clearInterval(intervalId);
  }, [fetchPolicies]);

  // Drag and Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      if (!title) {
        const nameWithoutExt = droppedFile.name.substring(0, droppedFile.name.lastIndexOf('.')) || droppedFile.name;
        setTitle(nameWithoutExt);
      }
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!title) {
        const nameWithoutExt = selectedFile.name.substring(0, selectedFile.name.lastIndexOf('.')) || selectedFile.name;
        setTitle(nameWithoutExt);
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setUploadError("Please select or drag a file to upload.");
      return;
    }
    if (!title.trim()) {
      setUploadError("Please enter a title.");
      return;
    }

    setUploading(true);
    setUploadError("");
    const formData = new FormData();
    formData.append("title", title);
    formData.append("file", file);

    try {
      await uploadCompanyPolicy(formData);
      setTitle("");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchPolicies();
    } catch (err) {
      setUploadError(err.response?.data?.error || "Failed to upload file.");
    } finally {
      setUploading(false);
    }
  };

  const handleStartEdit = (policy) => {
    setEditingId(policy.id);
    setEditTitle(policy.title);
    setEditFile(null);
    setEditError("");
  };

  const handleSaveEdit = async (id) => {
    if (!editTitle.trim()) {
      setEditError("Title cannot be empty.");
      return;
    }
    setUpdatingId(id);
    setEditError("");
    const formData = new FormData();
    formData.append("title", editTitle);
    if (editFile) {
      formData.append("file", editFile);
    }

    try {
      await updateCompanyPolicy(id, formData);
      setEditingId(null);
      setEditFile(null);
      fetchPolicies();
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to update policy.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeletePolicy = async (id) => {
    try {
      await deleteCompanyPolicy(id);
      setConfirmDeleteId(null);
      fetchPolicies();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const resolveFileUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${base}${path}`;
  };

  const getFileExtension = (fileName) => {
    return (fileName || "").split(".").pop().toLowerCase();
  };

  const getFileIcon = (fileName) => {
    const ext = getFileExtension(fileName);
    if (ext === "pdf") return "📄";
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext)) return "🖼️";
    if (["doc", "docx"].includes(ext)) return "📝";
    if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
    if (["ppt", "pptx"].includes(ext)) return "📉";
    return "📁";
  };

  const getIconColorClass = (fileName) => {
    const ext = getFileExtension(fileName);
    if (ext === "pdf") return "bg-rose-500/15 text-rose-300 border-rose-500/20";
    if (["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext)) return "bg-teal-500/15 text-teal-300 border-teal-500/20";
    if (["doc", "docx"].includes(ext)) return "bg-blue-500/15 text-blue-300 border-blue-500/20";
    if (["xls", "xlsx", "csv"].includes(ext)) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/20";
    if (["ppt", "pptx"].includes(ext)) return "bg-orange-500/15 text-orange-300 border-orange-500/20";
    return "bg-slate-500/15 text-slate-300 border-slate-500/20";
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 30 }}
        className="w-full max-w-4xl max-h-[85vh] bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden relative"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl">💼</span>
            <div>
              <h3 className="text-base font-bold text-white leading-none">Company Policies</h3>
              <p className="text-[11px] text-white/30 mt-1">Official handbooks and compliance documents</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
          >
            ✕
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* Admin Upload Section */}
          {isAdmin && (
            <GlassCard className="p-4 border border-white/10 bg-white/[0.02]">
              <h4 className="text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Upload New Policy</h4>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* File Upload Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all min-h-[120px]
                      ${isDragOver
                        ? "border-purple-400 bg-purple-500/10 text-purple-200"
                        : "border-white/10 hover:border-white/25 bg-white/[0.01] text-white/40"
                      }`}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {file ? (
                      <div className="space-y-1">
                        <span className="text-2xl">{getFileIcon(file.name)}</span>
                        <p className="text-xs font-bold text-white max-w-[250px] truncate">{file.name}</p>
                        <p className="text-[10px] text-white/45">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <span className="text-2xl text-purple-400">📤</span>
                        <p className="text-xs font-semibold text-white/65">Drag & drop policy file or click to browse</p>
                        <p className="text-[10px] text-white/30">Accepts all file types</p>
                      </div>
                    )}
                  </div>

                  {/* Title & Submit Area */}
                  <div className="flex flex-col justify-between space-y-4">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1 block">Policy Title</label>
                      <input
                        type="text"
                        placeholder="Enter policy name"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-purple-400 focus:bg-white/[0.08] transition-all"
                      />
                    </div>
                    {uploadError && (
                      <p className="text-[10px] text-rose-400 font-semibold">{uploadError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={uploading}
                      className="w-full py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-br from-purple-600 to-indigo-700 hover:from-purple-500 hover:to-indigo-600 border border-purple-500/30 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                    >
                      {uploading ? (
                        <>
                          <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <span>➕</span>
                          <span>Upload Policy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </GlassCard>
          )}

          {/* List Section */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-white/60">Policy Documents</h4>

            {loading ? (
              <Spinner />
            ) : policies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 border border-white/[0.06] rounded-2xl bg-white/[0.01]">
                <span className="text-2xl">📋</span>
                <p className="text-xs text-white/35 font-medium">No company policies uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-3.5">
                {policies.map((p) => {
                  const isItemEditing = editingId === p.id;
                  const isDeleting = confirmDeleteId === p.id;

                  return (
                    <div
                      key={p.id}
                      className="p-3 sm:p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] flex items-center justify-between hover:bg-white/[0.04] transition-all gap-4 flex-wrap sm:flex-nowrap"
                    >
                      {/* Left: Icon & Info */}
                      <div className="flex items-center gap-3.5 min-w-0 flex-1">
                        <span className={`w-10 h-10 rounded-xl border flex items-center justify-center text-lg flex-shrink-0 ${getIconColorClass(p.file_name)}`}>
                          {getFileIcon(p.file_name)}
                        </span>

                        {isItemEditing ? (
                          <div className="flex-1 space-y-2">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="w-full px-2 py-1 rounded bg-slate-800 border border-purple-400 text-white text-xs outline-none"
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                ref={editFileInputRef}
                                onChange={(e) => setEditFile(e.target.files[0])}
                                className="hidden"
                              />
                              <button
                                type="button"
                                onClick={() => editFileInputRef.current?.click()}
                                className="px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70 hover:text-white text-[10px] transition-all"
                              >
                                {editFile ? editFile.name : "Replace File (Optional)"}
                              </button>
                            </div>
                            {editError && <p className="text-[10px] text-rose-400 font-semibold">{editError}</p>}
                          </div>
                        ) : (
                          <div className="min-w-0">
                            <p className="text-white font-bold text-xs sm:text-sm truncate">{p.title}</p>
                            <p className="text-[10px] text-white/30 truncate mt-1">
                              {p.file_name} • Uploaded on {new Date(p.uploaded_at).toLocaleDateString("en-GB")} by {p.uploaded_by_name || "Admin"}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-end border-t border-white/5 pt-2 sm:pt-0 sm:border-0">
                        {isItemEditing ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(p.id)}
                              disabled={updatingId === p.id}
                              className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold transition-all"
                            >
                              {updatingId === p.id ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-[10px] font-bold transition-all"
                            >
                              Cancel
                            </button>
                          </>
                        ) : isDeleting ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-rose-300 font-bold">Delete?</span>
                            <button
                              onClick={() => handleDeletePolicy(p.id)}
                              className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-bold"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 text-[10px] font-bold"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <>
                            {/* Preview Button */}
                            <button
                              onClick={() => setPreviewFile({ title: p.title, url: resolveFileUrl(p.file_url), name: p.file_name })}
                              title="Preview document inline"
                              className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/12 border border-white/10 text-white/70 hover:text-white transition-all flex items-center gap-1.5 text-xs font-semibold"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              <span>Preview</span>
                            </button>
                            {/* Download Button */}
                            <a
                              href={resolveFileUrl(p.file_url)}
                              download
                              target="_blank"
                              rel="noreferrer"
                              title="Download document"
                              className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/12 border border-white/10 text-white/70 hover:text-white transition-all flex items-center gap-1.5 text-xs font-semibold"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                              <span>Download</span>
                            </a>
                            {/* Edit & Delete (Admin only) */}
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleStartEdit(p)}
                                  title="Edit Policy Title"
                                  className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/12 border border-white/10 text-white/70 hover:text-white transition-all flex items-center gap-1.5 text-xs font-semibold"
                                >
                                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  <span>Edit</span>
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(p.id)}
                                  title="Delete Policy"
                                  className="px-2.5 py-1.5 rounded-lg bg-rose-600/10 hover:bg-rose-600/20 border border-rose-500/20 text-rose-300 hover:text-rose-200 transition-all flex items-center gap-1.5 text-xs font-semibold"
                                >
                                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                  <span>Delete</span>
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Inline Preview Overlay Sub-Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/90 backdrop-blur-md">
          <div className="w-full max-w-5xl h-[90vh] bg-slate-900 border border-white/10 rounded-3xl flex flex-col overflow-hidden relative shadow-2xl">
            {/* Preview Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
              <div className="min-w-0">
                <h4 className="text-sm font-bold text-white truncate">{previewFile.title}</h4>
                <p className="text-[10px] text-white/40 truncate">{previewFile.name}</p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={previewFile.url}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="px-3.5 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  <span>Download</span>
                </a>
                <button
                  onClick={handleClosePreview}
                  className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
                >
                  ✕
                </button>
              </div>
            </div>
            {/* Preview Frame */}
            <div className="flex-1 bg-black/45 p-4 flex flex-col items-center justify-center overflow-auto relative min-h-[300px]">
              {previewLoading ? (
                <div className="text-center space-y-3">
                  <Spinner />
                  <p className="text-xs text-white/45">Loading document preview...</p>
                </div>
              ) : previewError ? (
                <div className="text-center p-6 space-y-4">
                  <span className="text-4xl">⚠️</span>
                  <p className="text-rose-300 text-sm font-bold">{previewError}</p>
                  <a
                    href={previewFile.url}
                    download
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>Download Document</span>
                  </a>
                </div>
              ) : getFileExtension(previewFile.name) === "pdf" ? (
                previewBlobUrl ? (
                  <iframe
                    src={`${previewBlobUrl}#toolbar=0`}
                    className="w-full h-full rounded-2xl border border-white/10 bg-white"
                    title="PDF Preview"
                  />
                ) : null
              ) : ["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(getFileExtension(previewFile.name)) ? (
                previewBlobUrl ? (
                  <img
                    src={previewBlobUrl}
                    alt={previewFile.title}
                    className="max-w-full max-h-full object-contain rounded-2xl shadow-lg"
                  />
                ) : null
              ) : (
                <div className="text-center p-6 space-y-4">
                  <span className="text-6xl block">{getFileIcon(previewFile.name)}</span>
                  <p className="text-white/85 text-sm font-bold">Preview not supported for this file format</p>
                  <p className="text-white/40 text-xs max-w-sm mx-auto">
                    We can't preview office files or general attachments directly in the browser. Please download the file to read it.
                  </p>
                  <a
                    href={previewFile.url}
                    download
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    <span>Download Document</span>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ANNOUNCEMENTS MODAL
// ════════════════════════════════════════════════════════════════════════════
function AnnouncementsModal({ role, announcements, loading, onRefresh, onClose }) {
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [selectedIcon, setSelectedIcon] = useState("📢");
  const [selectedBg, setSelectedBg] = useState("bg-purple-500/10 text-purple-400 border-purple-500/20");
  const [publishing, setPublishing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Location States
  const [locationsList, setLocationsList] = useState([]);
  const [isGlobal, setIsGlobal] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState([]);

  // Edit State
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editIcon, setEditIcon] = useState("📢");
  const [editBg, setEditBg] = useState("bg-purple-500/10 text-purple-400 border-purple-500/20");
  const [editIsGlobal, setEditIsGlobal] = useState(true);
  const [editSelectedLocations, setEditSelectedLocations] = useState([]);
  const [editError, setEditError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  // Delete State
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const isAdmin = role === "ADMIN";
  const displayedAnnouncements = isAdmin ? announcements : announcements.slice(0, 5);

  // Load locations for Admin selection
  useEffect(() => {
    if (isAdmin) {
      getLocations()
        .then((res) => {
          setLocationsList(res.data?.locations || []);
        })
        .catch((err) => {
          console.error("Failed to fetch locations in AnnouncementsModal", err);
        });
    }
  }, [isAdmin]);

  const ICON_THEMES = [
    { icon: "📢", label: "Broadcast", bg: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
    { icon: "🎉", label: "Celebration", bg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
    { icon: "⚠️", label: "Warning", bg: "bg-rose-500/10 text-rose-400 border-rose-500/20" },
    { icon: "💡", label: "Update/Info", bg: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
    { icon: "📅", label: "Event/Schedule", bg: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  ];

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) {
      setErrorMsg("Please enter a title.");
      return;
    }
    if (!newBody.trim()) {
      setErrorMsg("Please enter the announcement body.");
      return;
    }
    if (!isGlobal && selectedLocations.length === 0) {
      setErrorMsg("Please select at least one location or set to Global.");
      return;
    }

    setPublishing(true);
    setErrorMsg("");
    try {
      await createAnnouncement({
        title: newTitle.trim(),
        body: newBody.trim(),
        icon: selectedIcon,
        icon_bg: selectedBg,
        locations: isGlobal ? [] : selectedLocations,
      });
      setNewTitle("");
      setNewBody("");
      setSelectedIcon("📢");
      setSelectedBg("bg-purple-500/10 text-purple-400 border-purple-500/20");
      setIsGlobal(true);
      setSelectedLocations([]);
      onRefresh();
    } catch (err) {
      setErrorMsg(err.response?.data?.error || "Failed to publish announcement.");
    } finally {
      setPublishing(false);
    }
  };

  const handleStartEdit = (ann) => {
    setEditingId(ann.id);
    setEditTitle(ann.title);
    setEditBody(ann.body);
    setEditIcon(ann.icon || "📢");
    setEditBg(ann.icon_bg || "bg-purple-500/10 text-purple-400 border-purple-500/20");
    setEditError("");
    const annLocs = ann.locations || [];
    setEditIsGlobal(annLocs.length === 0);
    setEditSelectedLocations(annLocs);
  };

  const handleSaveEdit = async (id) => {
    if (!editTitle.trim()) {
      setEditError("Title cannot be empty.");
      return;
    }
    if (!editBody.trim()) {
      setEditError("Content body cannot be empty.");
      return;
    }
    if (!editIsGlobal && editSelectedLocations.length === 0) {
      setEditError("Please select at least one location or set to Global.");
      return;
    }

    setUpdatingId(id);
    setEditError("");
    try {
      await updateAnnouncement(id, {
        title: editTitle.trim(),
        body: editBody.trim(),
        icon: editIcon,
        icon_bg: editBg,
        locations: editIsGlobal ? [] : editSelectedLocations,
      });
      setEditingId(null);
      onRefresh();
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to update announcement.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await deleteAnnouncement(id);
      setConfirmDeleteId(null);
      onRefresh();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 30 }}
        className="w-full max-w-4xl max-h-[85vh] bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden relative"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xl">📢</span>
            <div>
              <h3 className="text-base font-bold text-white leading-none">Announcements</h3>
              <p className="text-[11px] text-white/30 mt-1">Latest broadcasts and updates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold"
          >
            ✕
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* Admin Publish Section */}
          {isAdmin && (
            <GlassCard className="p-4 border border-white/10 bg-white/[0.02]">
              <h4 className="text-xs font-bold uppercase tracking-wider text-white/60 mb-3">Create New Announcement</h4>
              <form onSubmit={handlePublish} className="space-y-4">
                <div className="space-y-3.5">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1 block">Title</label>
                    <input
                      type="text"
                      placeholder="Enter announcement title"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-purple-400 focus:bg-white/[0.08] transition-all"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1 block">Content Body</label>
                    <textarea
                      rows={3}
                      placeholder="Enter announcement message details..."
                      value={newBody}
                      onChange={(e) => setNewBody(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-xs outline-none focus:border-purple-400 focus:bg-white/[0.08] transition-all resize-none"
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-2 block">Theme / Category Icon</label>
                    <div className="flex flex-wrap gap-2 select-none">
                      {ICON_THEMES.map((theme) => {
                        const isSelected = selectedIcon === theme.icon;
                        return (
                          <button
                            key={theme.icon}
                            type="button"
                            onClick={() => {
                              setSelectedIcon(theme.icon);
                              setSelectedBg(theme.bg);
                            }}
                            className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all duration-200 border flex items-center gap-1.5 ${
                              isSelected
                                ? "bg-white/10 border-white text-white shadow-[0_0_12px_rgba(255,255,255,0.15)] ring-2 ring-black/90 ring-offset-1 ring-offset-[#0d1527]"
                                : "bg-white/[0.03] border-white/10 text-white/45 hover:bg-white/[0.08] hover:text-white/80 hover:border-white/20"
                            }`}
                          >
                            <span>{theme.icon}</span>
                            <span>{theme.label.toUpperCase()}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-2 block">Target Audience / Location</label>
                    <div className="flex flex-wrap gap-2 select-none">
                      {/* Global Pill */}
                      <button
                        type="button"
                        onClick={() => {
                          setIsGlobal(true);
                          setSelectedLocations([]);
                        }}
                        className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all duration-200 border flex items-center gap-1.5 ${
                          isGlobal
                            ? "bg-white/10 border-white text-white shadow-[0_0_12px_rgba(255,255,255,0.15)] ring-2 ring-black/90 ring-offset-1 ring-offset-[#0d1527]"
                            : "bg-white/[0.03] border-white/10 text-white/45 hover:bg-white/[0.08] hover:text-white/80 hover:border-white/20"
                        }`}
                      >
                        <span>🌐</span>
                        <span>GLOBAL (ALL)</span>
                      </button>

                      {/* Individual Location Pills */}
                      {locationsList.map((loc) => {
                        const isSelected = selectedLocations.includes(loc.code);
                        return (
                          <button
                            key={loc.code}
                            type="button"
                            onClick={() => {
                              const nextLocs = isSelected
                                ? selectedLocations.filter((c) => c !== loc.code)
                                : [...selectedLocations, loc.code];
                              setSelectedLocations(nextLocs);
                              setIsGlobal(nextLocs.length === 0);
                            }}
                            className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all duration-200 border flex items-center gap-1.5 ${
                              isSelected
                                ? "bg-white/10 border-white text-white shadow-[0_0_12px_rgba(255,255,255,0.15)] ring-2 ring-black/90 ring-offset-1 ring-offset-[#0d1527]"
                                : "bg-white/[0.03] border-white/10 text-white/45 hover:bg-white/[0.08] hover:text-white/80 hover:border-white/20"
                            }`}
                          >
                            <span>📍</span>
                            <span>{loc.code.toUpperCase()}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {errorMsg && (
                  <p className="text-[10px] text-rose-400 font-semibold">{errorMsg}</p>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={publishing}
                    className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-br from-purple-600 to-indigo-700 hover:from-purple-500 hover:to-indigo-600 border border-purple-500/30 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {publishing ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Publishing...</span>
                      </>
                    ) : (
                      <>
                        <span>📢</span>
                        <span>Publish Announcement</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </GlassCard>
          )}

          {/* List Section */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-white/60">Announcements Feed</h4>

            {loading ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : displayedAnnouncements.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 border border-white/[0.06] rounded-2xl bg-white/[0.01]">
                <span className="text-2xl">🔔</span>
                <p className="text-xs text-white/35 font-medium">No announcements published yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {displayedAnnouncements.map((a) => {
                  const isItemEditing = editingId === a.id;
                  const isDeleting = confirmDeleteId === a.id;

                  return (
                    <div
                      key={a.id}
                      className="p-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] flex items-start justify-between hover:bg-white/[0.04] transition-all gap-4 flex-wrap sm:flex-nowrap"
                    >
                      {/* Left: Icon & Content */}
                      <div className="flex items-start gap-3.5 min-w-0 flex-1">
                        {!isItemEditing && (
                          <span className={`w-10 h-10 rounded-xl border flex items-center justify-center text-lg flex-shrink-0 ${a.icon_bg || "bg-purple-500/10 text-purple-400 border-purple-500/20"}`}>
                            {a.icon || "📢"}
                          </span>
                        )}

                        {isItemEditing ? (
                          <div className="flex-1 space-y-3">
                            <div>
                              <label className="text-[9px] font-bold uppercase tracking-wider text-white/40 mb-1 block">Title</label>
                              <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-800 border border-purple-400/40 text-white text-xs outline-none focus:border-purple-400"
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold uppercase tracking-wider text-white/40 mb-1 block">Content Message</label>
                              <textarea
                                rows={2}
                                value={editBody}
                                onChange={(e) => setEditBody(e.target.value)}
                                className="w-full px-2.5 py-1.5 rounded-lg bg-slate-800 border border-purple-400/40 text-white text-xs outline-none focus:border-purple-400 resize-none"
                              />
                            </div>
                            <div>
                              <label className="text-[9px] font-bold uppercase tracking-wider text-white/40 mb-1.5 block">Theme Icon</label>
                              <div className="flex flex-wrap gap-1.5 select-none">
                                {ICON_THEMES.map((theme) => {
                                  const isSelected = editIcon === theme.icon;
                                  return (
                                    <button
                                      key={theme.icon}
                                      type="button"
                                      onClick={() => {
                                        setEditIcon(theme.icon);
                                        setEditBg(theme.bg);
                                      }}
                                      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-200 border flex items-center gap-1 ${
                                        isSelected
                                          ? "bg-white/10 border-white text-white shadow-[0_0_12px_rgba(255,255,255,0.15)] ring-2 ring-black/90 ring-offset-1 ring-offset-[#0d1527]"
                                          : "bg-white/[0.03] border-white/10 text-white/45 hover:bg-white/[0.08] hover:text-white/80 hover:border-white/20"
                                      }`}
                                    >
                                      <span>{theme.icon}</span>
                                      <span>{theme.label.toUpperCase()}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div>
                              <label className="text-[9px] font-bold uppercase tracking-wider text-white/40 mb-1.5 block">Target Audience / Location</label>
                              <div className="flex flex-wrap gap-1.5 select-none">
                                {/* Global Pill */}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditIsGlobal(true);
                                    setEditSelectedLocations([]);
                                  }}
                                  className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-200 border flex items-center gap-1 ${
                                    editIsGlobal
                                      ? "bg-white/10 border-white text-white shadow-[0_0_12px_rgba(255,255,255,0.15)] ring-2 ring-black/90 ring-offset-1 ring-offset-[#0d1527]"
                                      : "bg-white/[0.03] border-white/10 text-white/45 hover:bg-white/[0.08] hover:text-white/80 hover:border-white/20"
                                  }`}
                                >
                                  <span>🌐</span>
                                  <span>GLOBAL (ALL)</span>
                                </button>

                                {/* Individual Location Pills */}
                                {locationsList.map((loc) => {
                                  const isSelected = editSelectedLocations.includes(loc.code);
                                  return (
                                    <button
                                      key={loc.code}
                                      type="button"
                                      onClick={() => {
                                        const nextLocs = isSelected
                                          ? editSelectedLocations.filter((c) => c !== loc.code)
                                          : [...editSelectedLocations, loc.code];
                                        setEditSelectedLocations(nextLocs);
                                        setEditIsGlobal(nextLocs.length === 0);
                                      }}
                                      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-200 border flex items-center gap-1 ${
                                        isSelected
                                          ? "bg-white/10 border-white text-white shadow-[0_0_12px_rgba(255,255,255,0.15)] ring-2 ring-black/90 ring-offset-1 ring-offset-[#0d1527]"
                                          : "bg-white/[0.03] border-white/10 text-white/45 hover:bg-white/[0.08] hover:text-white/80 hover:border-white/20"
                                      }`}
                                    >
                                      <span>📍</span>
                                      <span>{loc.code.toUpperCase()}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            {editError && <p className="text-[10px] text-rose-400 font-semibold">{editError}</p>}
                          </div>
                        ) : (
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-bold text-xs sm:text-sm leading-snug">{a.title}</p>
                            <p className="text-[11px] text-white/70 mt-1.5 leading-relaxed whitespace-pre-wrap">{a.body}</p>
                            <p className="text-[10px] text-white/30 mt-2">
                              Published on {new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })} by {a.created_by_name || "Admin"}
                            </p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {(!a.locations || a.locations.length === 0) ? (
                                <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[9px] font-medium flex items-center gap-1">
                                  🌐 Global
                                </span>
                              ) : (
                                a.locations.map((locCode) => (
                                  <span key={locCode} className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20 text-[9px] font-medium flex items-center gap-1">
                                    📍 {locCode}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-end border-t border-white/5 pt-2.5 sm:pt-0 sm:border-0">
                        {isItemEditing ? (
                          <>
                            <button
                              onClick={() => handleSaveEdit(a.id)}
                              disabled={updatingId === a.id}
                              className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold transition-all"
                            >
                              {updatingId === a.id ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-[10px] font-bold transition-all"
                            >
                              Cancel
                            </button>
                          </>
                        ) : isDeleting ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-rose-300 font-bold">Delete?</span>
                            <button
                              onClick={() => handleDeleteAnnouncement(a.id)}
                              className="px-2 py-1 rounded bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-bold"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/70 text-[10px] font-bold"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <>
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleStartEdit(a)}
                                  title="Edit Announcement"
                                  className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/12 border border-white/10 text-white/70 hover:text-white transition-all flex items-center gap-1.5 text-xs font-semibold"
                                >
                                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  <span>Edit</span>
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteId(a.id)}
                                  title="Delete Announcement"
                                  className="px-2.5 py-1.5 rounded-lg bg-rose-600/10 hover:bg-rose-600/20 border border-rose-500/20 text-rose-300 hover:text-rose-200 transition-all flex items-center gap-1.5 text-xs font-semibold"
                                >
                                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                  <span>Delete</span>
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ANNOUNCEMENT DETAIL MODAL (POPUP)
// ════════════════════════════════════════════════════════════════════════════
function AnnouncementDetailModal({ announcement, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 30 }}
        className="w-full max-w-2xl bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden relative p-8 space-y-5"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <span className={`w-11 h-11 rounded-xl border flex items-center justify-center text-xl flex-shrink-0 ${announcement.icon_bg || "bg-purple-500/10 text-purple-400 border-purple-500/20"}`}>
              {announcement.icon || "📢"}
            </span>
            <div>
              <span className="px-2 py-0.5 rounded text-[9px] font-black tracking-widest bg-rose-500/20 text-rose-300 border border-rose-500/30 uppercase leading-none inline-block mb-1.5">
                Broadcast
              </span>
              <h3 className="text-base font-bold text-white sm:text-lg leading-snug">{announcement.title}</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold flex-shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Content Body */}
        <div className="border-t border-b border-white/5 py-5 max-h-[45vh] overflow-y-auto">
          <p className="text-white/90 text-sm sm:text-base leading-loose whitespace-pre-wrap">
            {announcement.body}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-white/40 pt-1">
          <span>Published by: <strong className="text-white/70">{announcement.created_by_name || "Admin"}</strong></span>
          <span>
            {new Date(announcement.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}
          </span>
        </div>

        {/* Close Action Button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl font-bold text-sm text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-all active:scale-95"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// HOLIDAYS LIST MODAL (POPUP)
// ════════════════════════════════════════════════════════════════════════════
function HolidaysModal({ role, onClose }) {
  const [holidays, setHolidays] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const hasLocationAccess = role === "ADMIN" || role === "MANAGER";



  // Load locations (Admins & Managers)
  useEffect(() => {
    if (hasLocationAccess) {
      getLocations()
        .then((res) => {
          setLocations(res.data?.locations || []);
        })
        .catch((err) => {
          console.error("Failed to load locations", err);
        });
    }
  }, [hasLocationAccess]);

  // Load holidays based on selected location
  const loadHolidays = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (hasLocationAccess && selectedLocation && selectedLocation !== "all") {
        params.location_id = selectedLocation;
      }
      const res = await apiClient.get("/attendance/api/holidays/", { params });
      setHolidays(res.data?.holidays || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load holidays.");
    } finally {
      setLoading(false);
    }
  }, [hasLocationAccess, selectedLocation]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Filter: holidays only (exclude WEEK_OFF), sorted by date ascending
  const filteredHolidays = useMemo(() => {
    return holidays
      .filter((h) => h.status !== "WEEK_OFF")
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [holidays]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 30 }}
        className="w-full max-w-3xl bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden relative p-6 sm:p-8 space-y-5 max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-violet-500/10 text-violet-400 border border-violet-500/20 flex items-center justify-center text-xl">
              📅
            </span>
            <div>
              <h3 className="text-base font-bold text-white sm:text-lg">Holidays List</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold flex-shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Filters Row — Location only */}
        {hasLocationAccess && (
          <div className="flex flex-wrap items-center gap-4 bg-white/5 p-3 rounded-2xl border border-white/5 flex-shrink-0">
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-white/75 whitespace-nowrap">Filter Location:</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="bg-slate-900 border border-white/10 text-white rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:border-violet-500 max-w-[150px]"
              >
                <option value="all">All Locations</option>
                {locations.map((loc) => (
                  <option key={loc.code} value={loc.code}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tabular Form */}
        <div className="flex-1 overflow-y-auto min-h-0 border border-white/5 rounded-2xl bg-white/5 no-scrollbar">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-white/10 bg-white/5 text-[11px] font-bold text-white/60 tracking-wider uppercase">
                <th className="py-3 px-4">S.No</th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-xs text-white/50">
                    <span className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin inline-block mr-2" />
                    Loading holidays list...
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-xs text-rose-400">
                    {error}
                  </td>
                </tr>
              )}

              {!loading && !error && filteredHolidays.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-xs text-white/40">
                    No holidays scheduled.
                  </td>
                </tr>
              )}

              {!loading && !error &&
                filteredHolidays.map((h, idx) => (
                  <tr key={h.id} className="border-b border-white/5 hover:bg-white/5 text-xs text-white/80 transition-colors">
                    <td className="py-3.5 px-4 font-semibold text-white/40">{idx + 1}</td>
                    <td className="py-3.5 px-4 font-bold text-white/90">
                      {new Date(h.date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${
                        h.status === "WEEK_OFF"
                          ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                          : "bg-rose-500/10 text-rose-400 border-rose-500/20"
                      }`}>
                        {h.status === "WEEK_OFF" ? "Week Off" : "Holiday"}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 max-w-[350px] truncate" title={h.description}>
                      {h.description}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end pt-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-all active:scale-95"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// MANAGE CL MODAL (SUPER ADMIN ONLY)
// ════════════════════════════════════════════════════════════════════════════
function ManageCLModal({ onClose }) {
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [detailedEmp, setDetailedEmp] = useState(null);
  const [formEligible, setFormEligible] = useState("");
  const [formAdjustment, setFormAdjustment] = useState(0);
  const [monthlyAdjs, setMonthlyAdjs] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load locations
  useEffect(() => {
    getLocations()
      .then((res) => setLocations(res.data?.locations || []))
      .catch((err) => console.error("Failed to load locations", err));
  }, []);

  // Fetch employees
  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        query: searchQuery,
        location: selectedLocation,
      };
      const res = await apiClient.get("/attendance/api/manage-cl/", { params });
      setEmployees(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load employees.");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedLocation]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleEdit = async (emp) => {
    setSelectedEmp(emp);
    setLoadingDetails(true);
    try {
      const res = await apiClient.get(`/attendance/api/manage-cl/?employee_id=${emp.employee_id}`);
      const data = res.data;
      setDetailedEmp(data);
      setFormEligible(data.cl_eligible_from || "");
      setFormAdjustment(data.cl_adjustment || 0);

      const adjs = {};
      (data.months || []).forEach(m => {
        adjs[`${m.year}_${m.month}`] = m.adjustment || 0;
      });
      setMonthlyAdjs(adjs);
    } catch (err) {
      alert("Failed to load employee details.");
      setSelectedEmp(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleMonthlyAdjChange = (year, month, val) => {
    setMonthlyAdjs(prev => ({
      ...prev,
      [`${year}_${month}`]: val
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedEmp) return;
    setSaving(true);
    try {
      const adjList = Object.entries(monthlyAdjs).map(([key, val]) => {
        const [year, month] = key.split("_");
        return {
          year: parseInt(year, 10),
          month: parseInt(month, 10),
          cl_adjustment: parseFloat(val) || 0
        };
      });

      await apiClient.post("/attendance/api/manage-cl/", {
        employee_id: selectedEmp.employee_id,
        cl_eligible_from: formEligible === "" ? "" : formEligible,
        cl_adjustment: parseFloat(formAdjustment) || 0,
        adjustments: adjList
      });
      setSelectedEmp(null);
      setDetailedEmp(null);
      loadEmployees();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save CL configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 30 }}
        className="w-full max-w-4xl bg-[#0d1527]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden relative p-6 sm:p-8 space-y-5 max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center text-xl">
              💵
            </span>
            <div>
              <h3 className="text-base font-bold text-white sm:text-lg">Manage Casual Leaves</h3>
              <p className="text-white/40 text-[11px] sm:text-xs font-semibold">Adjust eligibility start dates and apply manual balance adjustments (Super Admin Only)</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-all text-sm font-semibold flex-shrink-0"
          >
            ✕
          </button>
        </div>

        {/* Selected Employee Edit Form */}
        {selectedEmp && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            className="bg-white/5 p-4 rounded-2xl border border-indigo-500/30 flex-shrink-0 space-y-4 overflow-y-auto max-h-[40vh] no-scrollbar"
          >
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-bold text-indigo-300">
                Adjust CL: {selectedEmp.full_name} ({selectedEmp.employee_id})
              </h4>
              <button
                type="button"
                onClick={() => {
                  setSelectedEmp(null);
                  setDetailedEmp(null);
                }}
                className="text-white/55 hover:text-white text-xs font-bold"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              {loadingDetails ? (
                <div className="text-white/40 text-xs animate-pulse text-center py-4">Loading monthly details...</div>
              ) : (
                detailedEmp && (
                  <div className="space-y-4">
                    {/* General Config Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[11px] font-bold text-white/60 uppercase mb-1.5">CL Start Month</label>
                        <input
                          type="date"
                          value={formEligible}
                          onChange={(e) => setFormEligible(e.target.value)}
                          className="w-full bg-slate-900 border border-white/10 text-white rounded-xl px-3.5 py-2 text-xs focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-white/60 uppercase mb-1.5">Global CL Balance Adjustment</label>
                        <input
                          type="number"
                          step="0.5"
                          value={formAdjustment}
                          onChange={(e) => setFormAdjustment(e.target.value)}
                          placeholder="e.g. +1.0 or -1.5"
                          className="w-full bg-slate-900 border border-white/10 text-white rounded-xl px-3.5 py-2 text-xs focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    {/* Monthly Adjustments Table */}
                    <div className="border border-white/10 rounded-2xl overflow-hidden bg-white/5">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead>
                          <tr className="border-b border-white/10 text-white/50 uppercase font-black tracking-wider text-[9px] bg-white/[0.02]">
                            <th className="px-4 py-2">Month</th>
                            <th className="px-4 py-2 text-right">Base Limit</th>
                            <th className="px-4 py-2 text-center">Monthly Adjustment</th>
                            <th className="px-4 py-2 text-right">Total Limit</th>
                            <th className="px-4 py-2 text-right">Taken</th>
                            <th className="px-4 py-2 text-right font-bold text-indigo-300">Left (Unused)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(detailedEmp.months || []).map((m) => {
                            const key = `${m.year}_${m.month}`;
                            const currentAdj = monthlyAdjs[key] !== undefined ? monthlyAdjs[key] : 0;
                            const numAdj = parseFloat(currentAdj) || 0;
                            const totalLimit = m.base_limit + numAdj;
                            const unusedVal = Math.max(totalLimit - m.taken, 0);

                            const getMonthName = (monthNum) => {
                              const dateObj = new Date();
                              dateObj.setMonth(monthNum - 1);
                              return dateObj.toLocaleString("en-US", { month: "long" });
                            };

                            return (
                              <tr key={key} className="border-b border-white/5 hover:bg-white/[0.01] transition-all font-semibold text-white/70">
                                <td className="px-4 py-2">{getMonthName(m.month)} {m.year}</td>
                                <td className="px-4 py-2 text-right font-mono">{m.base_limit}</td>
                                <td className="px-4 py-2 text-center">
                                  <input
                                    type="number"
                                    step="0.5"
                                    value={currentAdj}
                                    onChange={(e) => handleMonthlyAdjChange(m.year, m.month, e.target.value)}
                                    className="w-20 bg-slate-900 border border-white/10 text-white rounded-lg px-2 py-1 text-xs text-center focus:outline-none focus:border-indigo-500 placeholder:text-white/20"
                                    placeholder="0.0"
                                  />
                                </td>
                                <td className="px-4 py-2 text-right font-mono text-white">{totalLimit.toFixed(1)}</td>
                                <td className="px-4 py-2 text-right font-mono text-rose-400">{m.taken}</td>
                                <td className="px-4 py-2 text-right font-mono font-bold text-indigo-400">{unusedVal.toFixed(1)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={saving}
                        className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/40 text-white rounded-xl py-2 px-6 text-xs font-bold transition-all border border-indigo-500/20"
                      >
                        {saving ? "Saving Changes..." : "Save Adjustments"}
                      </button>
                    </div>
                  </div>
                )
              )}
            </form>
          </motion.div>
        )}

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-4 bg-white/5 p-3 rounded-2xl border border-white/5 flex-shrink-0">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by Employee ID or Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-900 border border-white/10 text-white rounded-xl px-3.5 py-1.5 text-xs focus:outline-none focus:border-indigo-500 placeholder:text-white/30"
            />
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-white/75 whitespace-nowrap">Location:</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="bg-slate-900 border border-white/10 text-white rounded-xl px-3 py-1.5 text-xs focus:outline-none focus:border-indigo-500 min-w-[130px]"
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.code} value={loc.code}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabular Form */}
        <div className="flex-1 overflow-y-auto min-h-0 border border-white/5 rounded-2xl bg-white/5 no-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2">
              <span className="text-white/40 text-sm animate-pulse">Loading employees...</span>
            </div>
          ) : error ? (
            <div className="text-center text-red-400 py-10 font-bold text-xs">{error}</div>
          ) : employees.length === 0 ? (
            <div className="text-center text-white/40 py-10 text-xs font-semibold">No employees found.</div>
          ) : (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-white/10 text-white/50 uppercase font-black tracking-wider text-[10px] bg-white/[0.02]">
                  <th className="px-4 py-3">Employee ID</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Date Joined</th>
                  <th className="px-4 py-3">CL Start Date</th>
                  <th className="px-4 py-3 text-right">Adjustment</th>
                  <th className="px-4 py-3 text-right">Available CL</th>
                  <th className="px-4 py-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr
                    key={emp.employee_id}
                    className="border-b border-white/5 hover:bg-white/[0.02] text-white/80 transition-all font-semibold"
                  >
                    <td className="px-4 py-3 font-mono font-bold text-indigo-400">{emp.employee_id}</td>
                    <td className="px-4 py-3">{emp.full_name}</td>
                    <td className="px-4 py-3 uppercase">{emp.location || "GLOBAL"}</td>
                    <td className="px-4 py-3 text-white/50">{emp.date_joined || "—"}</td>
                    <td className="px-4 py-3 text-emerald-400">{emp.cl_eligible_from || "Default (Joined)"}</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-400">
                      {emp.cl_adjustment > 0 ? `+${emp.cl_adjustment}` : emp.cl_adjustment}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-white font-mono">
                      {emp.available} / {emp.limit}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleEdit(emp)}
                        className="bg-indigo-600/20 hover:bg-indigo-600 border border-indigo-500/30 text-indigo-300 hover:text-white px-2.5 py-1 rounded-lg text-[10px] font-black transition-all uppercase tracking-wider"
                      >
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}


export default ProfilePage;